import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { usePermissions } from '../hooks/usePermissions';

const C = {
  primary:      '#2E7D32',
  primaryDark:  '#1B5E20',
  primaryLight: '#43A047',
  secondary:    '#1565C0',
  success:      '#2E7D32',
  danger:       '#B71C1C',
  warning:      '#F57F17',
  orange:       '#E65100',
  amber:        '#F57F17',
  gray:         '#616161',
  grayLight:    '#9E9E9E',
  border:       '#E0E0E0',
  light:        '#F4F6F9',
  green50:      '#F1F8F2',
  green100:     '#E8F5E9',
  white:        '#FFFFFF',
  textDark:     '#212121',
  textMid:      '#616161',
};

const WORKFLOW_ORDRE = [
  'BROUILLON', 'SOUMIS', 'EN_MAINTENANCE', 'EN_ACHATS',
  'EN_CONTROLE', 'EN_COMPTABILITE', 'APPROUVE',
  'EN_EXECUTION', 'LIVRE', 'FACTURE_RECUE', 'PAYE', 'CLOTURE',
];

const STATUTS_CONFIG = {
  BROUILLON:       { label: 'Brouillon',        color: '#757575', bg: '#F5F5F5' },
  SOUMIS:          { label: 'Soumis',           color: '#1565C0', bg: '#E3F2FD' },
  EN_MAINTENANCE:  { label: 'En Maintenance',   color: '#6A1B9A', bg: '#F3E5F5' },
  EN_ACHATS:       { label: 'En Achats',        color: '#4527A0', bg: '#EDE7F6' },
  EN_CONTROLE:     { label: 'En Contrôle',      color: '#880E4F', bg: '#FCE4EC' },
  EN_COMPTABILITE: { label: 'En Comptabilité',  color: '#BF360C', bg: '#FFF3E0' },
  APPROUVE:        { label: 'Approuvé',         color: '#2E7D32', bg: '#E8F5E9' },
  EN_EXECUTION:    { label: 'En Exécution',     color: '#004D40', bg: '#E0F2F1' },
  LIVRE:           { label: 'Livré',            color: '#33691E', bg: '#F1F8E9' },
  FACTURE_RECUE:   { label: 'Facture Reçue',    color: '#E65100', bg: '#FFF8E1' },
  PAYE:            { label: '✓ Payé',           color: '#1B5E20', bg: '#E8F5E9' },
  CLOTURE:         { label: 'Clôturé',          color: '#546E7A', bg: '#ECEFF1' },
  REJETE:          { label: 'Rejeté',           color: '#B71C1C', bg: '#FFEBEE' },
};

function StatutBadge({ statut }) {
  const cfg = STATUTS_CONFIG[statut] || { label: statut, color: C.gray, bg: '#f5f5f5' };
  return (
    <span style={{ background: cfg.bg, color: cfg.color,
      borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

function formatMontant(v) {
  if (!v && v !== 0) return '—';
  return Number(v).toLocaleString('fr-FR') + ' FCFA';
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

function formatDateHeure(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Section Articles ──────────────────────────────────────────────────────────
function SectionArticles({ dossier, lignes, voitMontants = true }) {
  if (lignes && lignes.length > 0) {
    const total = lignes.reduce((s, l) => s + parseFloat(l.total_estime || 0), 0);
    const colonnes = voitMontants
      ? ['Désignation', 'Qté', 'Unité', 'Prix unitaire', 'Total']
      : ['Désignation', 'Qté', 'Unité'];
    return (
      <div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: C.primary }}>
              {colonnes.map(h => (
                <th key={h} style={{ color: C.white, padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => (
              <tr key={l.id} style={{ background: i % 2 === 0 ? C.white : '#f9f9f9', borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '8px 10px', fontWeight: 500 }}>{l.designation}</td>
                <td style={{ padding: '8px 10px' }}>{l.quantite}</td>
                <td style={{ padding: '8px 10px', color: C.gray }}>{l.unite}</td>
                {voitMontants && <td style={{ padding: '8px 10px' }}>{formatMontant(l.prix_unitaire_estime)}</td>}
                {voitMontants && <td style={{ padding: '8px 10px', fontWeight: 600, color: C.primary }}>{formatMontant(l.total_estime)}</td>}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: '#f5f5f5' }}>
              <td colSpan={voitMontants ? 4 : 3} style={{ padding: '10px', fontWeight: 700, fontSize: 13 }}>TOTAL ESTIMÉ</td>
              <td style={{ padding: '10px', fontWeight: 700, fontSize: 14, color: voitMontants ? C.primary : C.gray }}>
                {voitMontants ? formatMontant(total) : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }
  return (
    <div>
      {voitMontants && dossier.montant_estime > 0 && (
        <div style={{ fontWeight: 700, fontSize: 15, color: C.primary, marginBottom: 8 }}>
          💰 Montant estimé : {formatMontant(dossier.montant_estime)}
        </div>
      )}
      {dossier.description_besoins && (
        <div style={{ fontSize: 14, color: '#2c3e50', lineHeight: 1.6, marginBottom: 8 }}>
          {dossier.description_besoins}
        </div>
      )}
      <div style={{ fontSize: 12, color: C.gray, fontStyle: 'italic' }}>Détail des articles non renseigné</div>
    </div>
  );
}

// ── Timeline Workflow ─────────────────────────────────────────────────────────
function TimelineWorkflow({ dossier }) {
  const historique = dossier.historique || [];
  const statutsPassés = new Set(historique.map(h => h.statut_apres));
  const dateParStatut = {};
  historique.forEach(h => { if (h.created_at) dateParStatut[h.statut_apres] = h.created_at; });

  const estTravaux = dossier.type_dossier === 'TRAVAUX';
  const workflow = WORKFLOW_ORDRE.filter(s => s !== 'EN_MAINTENANCE' || estTravaux);

  const statutActuel = dossier.statut;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {workflow.map((statut, i) => {
        const passe = statutsPassés.has(statut) || statut === 'BROUILLON';
        const actuel = statut === statutActuel;
        const cfg = STATUTS_CONFIG[statut] || { label: statut, color: C.gray };

        let cercleStyle = {
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700,
        };
        let labelStyle = { fontSize: 13, fontWeight: actuel ? 700 : 400 };

        if (actuel) {
          cercleStyle = { ...cercleStyle, background: cfg.bg, border: `2px solid ${cfg.color}`, color: cfg.color };
          labelStyle = { ...labelStyle, color: cfg.color };
        } else if (passe) {
          cercleStyle = { ...cercleStyle, background: '#e8f5e9', border: '2px solid #2e7d32', color: '#2e7d32' };
        } else {
          cercleStyle = { ...cercleStyle, background: '#f5f5f5', border: '2px solid #bdbdbd', color: '#bdbdbd' };
          labelStyle = { ...labelStyle, color: '#bdbdbd' };
        }

        return (
          <div key={statut} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={cercleStyle}>
                {actuel ? '⏳' : passe ? '✓' : ''}
              </div>
              {i < workflow.length - 1 && (
                <div style={{ width: 2, height: 18, background: passe ? '#a5d6a7' : '#e0e0e0' }} />
              )}
            </div>
            <div style={{ paddingTop: 4, paddingBottom: i < workflow.length - 1 ? 0 : 0 }}>
              <div style={labelStyle}>{cfg.label}</div>
              {(passe || actuel) && dateParStatut[statut] && (
                <div style={{ fontSize: 11, color: C.gray }}>{formatDate(dateParStatut[statut])}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Proformats ────────────────────────────────────────────────────────────────
function SectionProformats({ dossier, seuilProformats, monRole, dossierId, onRefresh, showFormProformat, setShowFormProformat, formProformat, setFormProformat, fournisseurs, navigate, avisControle = [], voitMontants = true, peutVoirPDF = true, peutEcrire = false, proformatEnEdition, setProformatEnEdition, onModifier, onSupprimer, onSauvegarder }) {
  const proformats = dossier.proformats || [];
  const manque = seuilProformats - proformats.length;
  const peutAjouter = dossier.statut === 'EN_ACHATS' && ['service_achats','responsable_achats','admin','super_admin'].includes(monRole);
  const [modeLibre, setModeLibre] = useState(false);

  const selectionner = async (pId) => {
    try {
      await api.post(`/achats/proformats/${pId}/selectionner`, {});
      onRefresh();
    } catch (e) { alert(e.response?.data?.message || 'Erreur'); }
  };

  const soumettre = async () => {
    if (!modeLibre && !formProformat.fournisseur_id) { alert('Sélectionnez un fournisseur'); return; }
    if (modeLibre && !formProformat.fournisseur_nom) { alert('Saisissez le nom du fournisseur'); return; }
    if (!formProformat.numero_proformat) { alert('Le numéro de proformat est requis'); return; }
    if (!formProformat.montant_ttc) { alert('Le montant TTC est requis'); return; }
    try {
      const fd = new FormData();
      if (modeLibre) {
        fd.append('fournisseur_nom', formProformat.fournisseur_nom);
      } else {
        fd.append('fournisseur_id', formProformat.fournisseur_id);
      }
      fd.append('numero_proformat', formProformat.numero_proformat);
      fd.append('montant_ttc', formProformat.montant_ttc);
      if (formProformat.date_proformat) fd.append('date_proformat', formProformat.date_proformat);
      if (formProformat.fichier) fd.append('fichier', formProformat.fichier);
      await api.post(`/achats/dossiers/${dossierId}/proformats`, fd);
      setFormProformat({ fournisseur_id: '', fournisseur_nom: '', numero_proformat: '', montant_ttc: '', date_proformat: '', fichier: null });
      setShowFormProformat(false);
      setModeLibre(false);
      onRefresh();
    } catch (e) { alert(e.response?.data?.message || 'Erreur'); }
  };

  return (
    <div>
      {/* Bandeau urgence */}
      {dossier.urgence && (
        <div style={{ background: '#fff8e1', border: '1px solid #f9a825', borderRadius: 8,
          padding: '8px 14px', marginBottom: 12, fontSize: 13, color: '#e65100', fontWeight: 600 }}>
          ⚡ Dossier urgent — le seuil de proformats peut être contourné
        </div>
      )}

      {/* Bandeau manque proformats (seulement si pas urgent) */}
      {!dossier.urgence && manque > 0 && (
        <div style={{ background: '#fff3e0', border: '1px solid #f57c00', borderRadius: 8,
          padding: '8px 14px', marginBottom: 12, fontSize: 13, color: '#e65100' }}>
          ⚠️ Il manque <strong>{manque}</strong> proformat(s). Minimum requis : {seuilProformats}
        </div>
      )}

      {/* Bouton ajouter */}
      {peutAjouter && !showFormProformat && (
        <button onClick={() => {
          const codeAbattoir = dossier?.numero?.split('-')[2] || 'ABT';
          const annee = new Date().getFullYear();
          const seq = String(proformats.length + 1).padStart(3, '0');
          const numeroAuto = `PRO-${codeAbattoir}-${annee}-${seq}`;
          setFormProformat({ fournisseur_id: '', fournisseur_nom: '', numero_proformat: numeroAuto, montant_ttc: '', date_proformat: '', fichier: null });
          setModeLibre(false);
          setShowFormProformat(true);
        }}
          style={{ background: C.secondary, color: C.white, border: 'none', padding: '7px 16px',
            borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
          + Ajouter un proformat
        </button>
      )}

      {/* Formulaire inline */}
      {showFormProformat && (
        <div style={{ background: '#e3f2fd', border: `1px solid ${C.secondary}`, borderRadius: 8,
          padding: 14, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 11, color: C.gray, marginBottom: 3 }}>Fournisseur *</div>
              {!modeLibre ? (
                <>
                  <select value={formProformat.fournisseur_id} onChange={e => setFormProformat({...formProformat, fournisseur_id: e.target.value})}
                    style={{ width: '100%', padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: C.white }}>
                    <option value="">-- Sélectionner un fournisseur --</option>
                    {fournisseurs.map(f => (
                      <option key={f.id} value={f.id}>{f.nom}{f.ninea ? ` (${f.ninea})` : ''}</option>
                    ))}
                  </select>
                  {fournisseurs.length === 0 && (
                    <small style={{ color: '#e65100', fontSize: 11 }}>
                      Aucun fournisseur enregistré.{' '}
                      <span style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => navigate('/achats?tab=fournisseurs')}>
                        Ajouter un fournisseur d'abord →
                      </span>
                    </small>
                  )}
                  <small style={{ fontSize: 11, color: C.secondary, cursor: 'pointer', textDecoration: 'underline', display: 'block', marginTop: 4 }}
                    onClick={() => { setModeLibre(true); setFormProformat({...formProformat, fournisseur_id: ''}); }}>
                    Fournisseur non référencé ? Saisir manuellement →
                  </small>
                </>
              ) : (
                <>
                  <input placeholder="Nom du fournisseur *" value={formProformat.fournisseur_nom}
                    onChange={e => setFormProformat({...formProformat, fournisseur_nom: e.target.value})}
                    style={{ width: '100%', padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginBottom: 6 }} />
                  <small style={{ fontSize: 11, color: C.secondary, cursor: 'pointer', textDecoration: 'underline', display: 'block' }}
                    onClick={() => { setModeLibre(false); setFormProformat({...formProformat, fournisseur_nom: ''}); }}>
                    ← Choisir dans la liste
                  </small>
                </>
              )}
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.gray, marginBottom: 3 }}>N° proformat *</div>
              <input placeholder="ex: PRO-2026-001" value={formProformat.numero_proformat} onChange={e => setFormProformat({...formProformat, numero_proformat: e.target.value})}
                style={{ width: '100%', padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
              <small style={{ color: '#888', fontSize: 11 }}>Numéro auto-généré — remplacez par celui du fournisseur si disponible</small>
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.gray, marginBottom: 3 }}>Montant TTC (FCFA) *</div>
              <input type="number" min="0" value={formProformat.montant_ttc} onChange={e => setFormProformat({...formProformat, montant_ttc: e.target.value})}
                style={{ width: '100%', padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.gray, marginBottom: 3 }}>Date proformat</div>
              <input type="date" value={formProformat.date_proformat} onChange={e => setFormProformat({...formProformat, date_proformat: e.target.value})}
                style={{ width: '100%', padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.gray, marginBottom: 3 }}>Fichier PDF</div>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFormProformat({...formProformat, fichier: e.target.files[0]})}
                style={{ fontSize: 12 }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={soumettre}
              style={{ background: C.success, color: C.white, border: 'none', padding: '7px 18px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              Enregistrer
            </button>
            <button onClick={() => { setShowFormProformat(false); setModeLibre(false); }}
              style={{ background: C.gray, color: C.white, border: 'none', padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Liste proformats */}
      {proformats.length === 0 ? (
        <div style={{ color: C.gray, fontSize: 13, fontStyle: 'italic' }}>Aucun proformat soumis</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {proformats.map(p => {
            const selectionne = p.selectionne;
            const enEdition = proformatEnEdition?.id === p.id;
            const nomFournisseur = p.fournisseur_nom || p.fournisseur_nom_libre || p.numero_proformat || `Proformat #${p.id}`;
            return (
              <div key={p.id} style={{
                padding: '12px 14px', borderRadius: 8,
                background: selectionne ? '#e8f5e9' : C.white,
                border: `${selectionne ? '2px' : '1px'} solid ${selectionne ? '#4caf50' : C.border}`,
              }}>
                {/* Ligne principale */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{nomFournisseur}</div>
                    {p.date_proformat && <div style={{ fontSize: 11, color: C.gray }}>{formatDate(p.date_proformat)}</div>}
                  </div>
                  {voitMontants ? (
                    <div style={{ fontWeight: 700, color: C.primary, fontSize: 13 }}>
                      {p.montant_ttc ? formatMontant(p.montant_ttc) : (p.montant_ht ? formatMontant(p.montant_ht) + ' HT' : '—')}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: C.gray }}>💼 Offre reçue</div>
                  )}
                  {p.fichier_url && peutVoirPDF && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <a href={p.fichier_url} target="_blank" rel="noreferrer"
                        style={{ fontSize: 12, color: '#fff', background: '#1565c0', textDecoration: 'none', borderRadius: 4, padding: '4px 8px', fontWeight: 600 }}>
                        📄 Voir
                      </a>
                      <a href={p.fichier_url} download
                        style={{ fontSize: 12, color: '#fff', background: '#2e7d32', textDecoration: 'none', borderRadius: 4, padding: '4px 8px', fontWeight: 600 }}>
                        ⬇️
                      </a>
                    </div>
                  )}
                  {!p.fichier_url && peutVoirPDF && (
                    <span style={{ fontSize: 11, color: '#f44336', fontStyle: 'italic' }}>⚠️ Pas de PDF</span>
                  )}
                  {selectionne && (
                    <span style={{ fontSize: 12, color: '#2e7d32', fontWeight: 700, background: '#c8e6c9', borderRadius: 10, padding: '3px 10px' }}>✅ Retenu</span>
                  )}
                  {/* Actions EN_ACHATS */}
                  {dossier.statut === 'EN_ACHATS' && peutEcrire && (
                    <div style={{ display: 'flex', gap: 5, marginLeft: 'auto' }}>
                      {!selectionne && (
                        <button onClick={() => selectionner(p.id)}
                          style={{ fontSize: 12, background: '#ff9800', color: 'white', border: 'none', borderRadius: 5, padding: '4px 9px', cursor: 'pointer', fontWeight: 600 }}>
                          ★ Retenir
                        </button>
                      )}
                      <button onClick={() => onModifier(p)}
                        style={{ fontSize: 12, background: '#1976d2', color: 'white', border: 'none', borderRadius: 5, padding: '4px 9px', cursor: 'pointer', fontWeight: 600 }}>
                        ✏️ Modifier
                      </button>
                      {!selectionne && (
                        <button onClick={() => onSupprimer(p.id)}
                          style={{ fontSize: 12, background: '#d32f2f', color: 'white', border: 'none', borderRadius: 5, padding: '4px 9px', cursor: 'pointer', fontWeight: 600 }}>
                          🗑️
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Formulaire de modification inline */}
                {enEdition && (
                  <div style={{ marginTop: 12, padding: 14, background: '#f5f5f5', borderRadius: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 11, color: '#555', fontWeight: 600, marginBottom: 4 }}>Fournisseur</div>
                        {fournisseurs.length > 0 ? (
                          <select value={proformatEnEdition.fournisseur_id}
                            onChange={e => setProformatEnEdition({ ...proformatEnEdition, fournisseur_id: e.target.value, fournisseur_nom_libre: '' })}
                            style={{ width: '100%', padding: '7px', borderRadius: 6, border: '1px solid #ddd' }}>
                            <option value="">-- Saisie libre --</option>
                            {fournisseurs.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
                          </select>
                        ) : null}
                        {!proformatEnEdition.fournisseur_id && (
                          <input placeholder="Nom fournisseur libre" value={proformatEnEdition.fournisseur_nom_libre}
                            onChange={e => setProformatEnEdition({ ...proformatEnEdition, fournisseur_nom_libre: e.target.value })}
                            style={{ width: '100%', padding: '7px', borderRadius: 6, border: '1px solid #ddd', marginTop: 4, boxSizing: 'border-box' }} />
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#555', fontWeight: 600, marginBottom: 4 }}>Montant TTC (FCFA) *</div>
                        <input type="number" value={proformatEnEdition.montant_ttc}
                          onChange={e => setProformatEnEdition({ ...proformatEnEdition, montant_ttc: e.target.value })}
                          style={{ width: '100%', padding: '7px', borderRadius: 6, border: '1px solid #ddd', boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#555', fontWeight: 600, marginBottom: 4 }}>N° Proformat</div>
                        <input type="text" value={proformatEnEdition.numero_proformat}
                          onChange={e => setProformatEnEdition({ ...proformatEnEdition, numero_proformat: e.target.value })}
                          style={{ width: '100%', padding: '7px', borderRadius: 6, border: '1px solid #ddd', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 11, color: '#555', fontWeight: 600, marginBottom: 4 }}>Nouveau PDF (optionnel)</div>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                          onChange={e => setProformatEnEdition({ ...proformatEnEdition, fichier: e.target.files[0] })}
                          style={{ fontSize: 12 }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={onSauvegarder}
                        style={{ background: '#388e3c', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        💾 Sauvegarder
                      </button>
                      <button onClick={() => setProformatEnEdition(null)}
                        style={{ background: '#757575', color: 'white', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13 }}>
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {/* Avis contrôle */}
                {avisControle.find(a => a.proformat_id === p.id) && (() => {
                  const av = avisControle.find(a => a.proformat_id === p.id);
                  const cfg = av.avis === 'conforme' ? { bg: '#e8f5e9', label: '✅ Conforme' }
                    : av.avis === 'non_conforme' ? { bg: '#ffebee', label: '❌ Non conforme' }
                    : { bg: '#fff8e1', label: '⚠️ Réserve' };
                  return (
                    <div style={{ background: cfg.bg, borderRadius: 4, padding: '8px 12px', fontSize: 12, marginTop: 8 }}>
                      <span style={{ fontWeight: 600 }}>{cfg.label}</span>
                      {av.auteur_nom && <span> — {av.auteur_nom}</span>}
                      {av.commentaire && <div style={{ color: '#555', marginTop: 4, fontStyle: 'italic' }}>"{av.commentaire}"</div>}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Contrôle Proformats (EN_CONTROLE) ─────────────────────────────────────────
function SectionControleProformats({ dossier, dossierId, monRole, avisParProformat, setAvisParProformat, commentaireControle, setCommentaireControle, onRefresh }) {
  const proformats = dossier.proformats || [];
  const peutContrôler = dossier.statut === 'EN_CONTROLE' && ['verificateur', 'controle_gestion', 'admin', 'super_admin'].includes(monRole);
  if (!peutContrôler) return null;

  // Séparation des rôles
  const ROLES_PEUT_RETENIR = ['service_achats', 'responsable_achats', 'admin', 'super_admin'];
  const peutChangerRetenu = ROLES_PEUT_RETENIR.includes(monRole);
  const estAnalyste = ['verificateur', 'controle_gestion', 'comptable', 'comptabilite', 'direction'].includes(monRole);

  const setAvis = (proformatId, champ, valeur) => {
    setAvisParProformat(prev => ({
      ...prev,
      [proformatId]: { ...(prev[proformatId] || {}), [champ]: valeur },
    }));
  };

  const handleSoumettreAvis = async (proformatId, extraData = {}) => {
    try {
      const avis = avisParProformat[proformatId] || {};
      await api.post(`/achats/dossiers/${dossierId}/proformats/${proformatId}/avis`, {
        avis: avis.avis || 'conforme',
        commentaire: avis.commentaire || '',
        propose_retenu: false,
        ...extraData,
      });
      onRefresh();
    } catch (e) { alert(e.response?.data?.message || 'Erreur'); }
  };

  const handleSelectionnerProformat = async (proformatId) => {
    try {
      const avis = avisParProformat[proformatId] || {};
      await api.post(`/achats/dossiers/${dossierId}/proformats/${proformatId}/avis`, {
        avis: avis.avis || 'conforme',
        commentaire: avis.commentaire || '',
        propose_retenu: true,
      });
      onRefresh();
    } catch (e) { alert(e.response?.data?.message || 'Erreur'); }
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ background: '#e8eaf6', border: '1px solid #5c6bc0', borderRadius: 8,
        padding: '8px 14px', marginBottom: 14, fontSize: 13, color: '#1a237e', fontWeight: 600 }}>
        🔍 Mode contrôle — Donnez votre avis sur chaque proformat
        {estAnalyste && (
          <span style={{ fontWeight: 400, color: '#5c6bc0', marginLeft: 8, fontSize: 12 }}>
            (vous pouvez suggérer un fournisseur — la décision finale revient au Service Achats)
          </span>
        )}
      </div>

      {proformats.length === 0 && (
        <div style={{ color: C.gray, fontSize: 13, fontStyle: 'italic' }}>Aucun proformat à évaluer</div>
      )}

      {proformats.map(p => {
        const a = avisParProformat[p.id] || {};
        const nomFournisseur = p.fournisseur_nom || p.fournisseur_nom_libre || p.numero_proformat || `Proformat #${p.id}`;
        return (
          <div key={p.id} style={{
            border: `1px solid ${p.selectionne ? '#a5d6a7' : C.border}`,
            background: p.selectionne ? '#f1f8e9' : C.white,
            borderRadius: 8, padding: 14, marginBottom: 12,
          }}>
            {/* En-tête proformat */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>{nomFournisseur}</span>
              <span style={{ fontWeight: 700, color: C.primary, fontSize: 13 }}>
                {p.montant_ttc ? formatMontant(p.montant_ttc) : '—'}
              </span>
              {p.selectionne && (
                <span style={{ fontSize: 12, color: '#2e7d32', fontWeight: 700, background: '#c8e6c9',
                  borderRadius: 10, padding: '3px 10px' }}>✅ Retenu</span>
              )}
              {p.fichier_url ? (
                <div style={{ display: 'flex', gap: 4 }}>
                  <a href={p.fichier_url} target="_blank" rel="noreferrer"
                    style={{ fontSize: 12, color: '#fff', background: '#1565c0', textDecoration: 'none',
                      borderRadius: 4, padding: '4px 8px', fontWeight: 600 }}>
                    📄 Voir PDF
                  </a>
                  <a href={p.fichier_url} download
                    style={{ fontSize: 12, color: '#fff', background: '#2e7d32', textDecoration: 'none',
                      borderRadius: 4, padding: '4px 8px', fontWeight: 600 }}>
                    ⬇️ Télécharger
                  </a>
                </div>
              ) : (
                <span style={{ fontSize: 11, color: '#f44336', fontStyle: 'italic' }}>⚠️ Aucun PDF joint</span>
              )}
            </div>

            {/* Radio avis */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 13 }}>
              {[['conforme', '✅ Conforme'], ['non_conforme', '❌ Non conforme'], ['reserve', '⚠️ Réserve']].map(([val, label]) => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <input type="radio" name={`avis-${p.id}`} value={val} checked={a.avis === val}
                    onChange={() => setAvis(p.id, 'avis', val)} />
                  {label}
                </label>
              ))}
            </div>

            {/* Commentaire */}
            <textarea
              placeholder="Observations sur cette offre..."
              value={a.commentaire || ''}
              onChange={e => setAvis(p.id, 'commentaire', e.target.value)}
              style={{ width: '100%', minHeight: 60, padding: '7px 9px', border: `1px solid ${C.border}`,
                borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical', marginBottom: 8 }}
            />

            {/* ── Boutons selon rôle ── */}

            {/* Service Achats : peut changer le retenu */}
            {peutChangerRetenu && !p.selectionne && (
              <button
                onClick={() => handleSelectionnerProformat(p.id)}
                style={{ background: '#ff9800', color: 'white', border: 'none',
                  borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
              >
                ★ Retenir ce fournisseur
              </button>
            )}

            {/* Analyste (vérificateur/contrôle_gestion/comptable...) : peut suggérer */}
            {estAnalyste && (
              <div style={{ marginTop: 10 }}>
                {a.showProposition ? (
                  <div style={{ background: '#e8f5e9', borderRadius: 8, padding: 12, marginTop: 8, border: '1px solid #a5d6a7' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#2e7d32', marginBottom: 8 }}>
                      💡 Votre proposition alternative
                    </div>
                    <textarea
                      placeholder="Argumentez votre proposition : pourquoi ce fournisseur serait meilleur ? (qualité, délai, certifications...)"
                      value={a.proposition_alternative || ''}
                      onChange={e => setAvis(p.id, 'proposition_alternative', e.target.value)}
                      rows={3}
                      style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #a5d6a7',
                        fontSize: 12, resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }}
                    />
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 8, fontStyle: 'italic' }}>
                      ℹ️ Ceci est une suggestion. Le Service Achats et la Direction prendront la décision finale.
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleSoumettreAvis(p.id, {
                          proposition_alternative: a.proposition_alternative,
                          propose_proformat_id: p.id
                        })}
                        style={{ background: '#2e7d32', color: 'white', border: 'none',
                          borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        💾 Enregistrer la suggestion
                      </button>
                      <button
                        onClick={() => setAvis(p.id, 'showProposition', false)}
                        style={{ background: '#757575', color: 'white', border: 'none',
                          borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {!p.selectionne && (
                      <button
                        onClick={() => setAvis(p.id, 'showProposition', true)}
                        style={{ background: 'white', color: '#2e7d32', border: '1px solid #2e7d32',
                          borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, marginTop: 6 }}
                      >
                        💡 Suggérer ce fournisseur (avec arguments)
                      </button>
                    )}
                    {a.proposition_alternative && (
                      <div style={{ background: '#f1f8e9', borderRadius: 6, padding: '8px 12px', marginTop: 8,
                        border: '1px solid #c8e6c9', fontSize: 12 }}>
                        <span style={{ fontWeight: 600, color: '#2e7d32' }}>💡 Votre suggestion :</span>
                        <span style={{ color: '#444', marginLeft: 6, fontStyle: 'italic' }}>
                          "{a.proposition_alternative}"
                        </span>
                        <button
                          onClick={() => setAvis(p.id, 'showProposition', true)}
                          style={{ background: 'none', border: 'none', color: '#1976d2',
                            cursor: 'pointer', fontSize: 11, marginLeft: 8 }}
                        >
                          ✏️ Modifier
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Commentaire général */}
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 12, color: C.gray, marginBottom: 4 }}>Commentaire général du contrôle</div>
        <textarea
          placeholder="Commentaire général du contrôle de gestion..."
          value={commentaireControle}
          onChange={e => setCommentaireControle(e.target.value)}
          style={{ width: '100%', minHeight: 80, padding: '10px', borderRadius: 6,
            border: `1px solid ${C.border}`, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }}
        />
      </div>
    </div>
  );
}

// ── Documents ─────────────────────────────────────────────────────────────────
function SectionDocuments({ dossier, proformats = [], peutEcrire = false, onRefresh }) {
  const s = dossier.statut;
  // La facture est stockée dans factures_definitives, pas dans dossiers_achat.facture_url
  const factureUrl = (dossier.factures || [])[0]?.fichier_url || null;
  const bordereauUrl = dossier.bordereau_livraison_url || null;

  const lienStyle = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
    borderRadius: 8, marginBottom: 8, textDecoration: 'none', fontSize: 14 };
  const absenceStyle = { padding: '10px 14px', borderRadius: 8, marginBottom: 8, fontSize: 13, fontStyle: 'italic' };

  // Statuts "avancés" où les documents sont obligatoires
  const STATUTS_AVANCES = ['FACTURE_RECUE','PAYE','CLOTURE'];
  const statutAvance = STATUTS_AVANCES.includes(s);

  const docManquants = [];
  if (!bordereauUrl) docManquants.push('Bordereau de livraison — Non joint');
  if (!factureUrl)   docManquants.push('Facture fournisseur — Non reçue');

  const handleUploadFacture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('facture', file);
    try {
      await api.post(`/achats/dossiers/${dossier.id}/facture`, fd);
      if (onRefresh) onRefresh();
    } catch (err) {
      alert('Erreur upload facture : ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <div>
      {/* Alerte documents manquants (statuts >= FACTURE_RECUE) */}
      {statutAvance && docManquants.length > 0 && (
        <div style={{
          background: '#FFEBEE', border: '1px solid #FFCDD2',
          borderRadius: 8, padding: 12, marginBottom: 12,
          color: '#C62828', fontSize: 12,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠ Documents manquants</div>
          <div style={{ marginBottom: 4 }}>Les documents suivants sont requis :</div>
          {docManquants.map(d => (
            <div key={d} style={{ paddingLeft: 8 }}>• {d}</div>
          ))}
        </div>
      )}

      {/* Bordereau de livraison */}
      {bordereauUrl ? (
        <a href={bordereauUrl} target="_blank" rel="noreferrer"
          style={{ ...lienStyle, background: '#e8f5e9', color: '#2e7d32' }}>
          📦 Bordereau de livraison
          <span style={{ fontSize: 11, color: '#888', marginLeft: 'auto' }}>Voir →</span>
        </a>
      ) : (
        <div style={{ ...absenceStyle, background: '#f5f5f5', color: '#999' }}>
          📦 Bordereau de livraison — Non joint
        </div>
      )}

      {/* Facture fournisseur */}
      {factureUrl ? (
        <a href={factureUrl} target="_blank" rel="noreferrer"
          style={{ ...lienStyle, background: '#e8f5e9', color: '#2e7d32' }}>
          🧾 Facture fournisseur
          <span style={{ fontSize: 11, color: '#888', marginLeft: 'auto' }}>Voir →</span>
        </a>
      ) : peutEcrire && s === 'EN_COMPTABILITE' ? (
        <div style={{ padding: '12px 14px', background: '#fff8e1', border: '2px dashed #ffc107', borderRadius: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 13, color: '#f57f17', marginBottom: 8, fontWeight: 600 }}>
            🧾 Facture fournisseur — À joindre avant approbation
          </div>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleUploadFacture} style={{ fontSize: 13 }} />
        </div>
      ) : (
        <div style={{ ...absenceStyle, background: '#fff3e0', border: '1px solid #ffcc02', color: '#e65100' }}>
          ⚠️ Facture fournisseur — Non reçue
        </div>
      )}

      {/* Proformats PDF */}
      {proformats.filter(p => p.fichier_url).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase' }}>
            Proformats reçus :
          </div>
          {proformats.map(p => p.fichier_url && (
            <a key={p.id} href={p.fichier_url} target="_blank" rel="noreferrer"
              style={{ ...lienStyle, background: '#f3e5f5', color: '#6a1b9a', marginBottom: 6 }}>
              📋 {p.fournisseur_nom_libre || p.numero_proformat || `Proformat #${p.id}`}
              {p.selectionne && (
                <span style={{ background: '#4caf50', color: 'white', fontSize: 10, padding: '2px 6px', borderRadius: 10 }}>
                  ✅ Retenu
                </span>
              )}
              <span style={{ fontSize: 11, color: '#888', marginLeft: 'auto' }}>Voir PDF →</span>
            </a>
          ))}
        </div>
      )}

      {!bordereauUrl && !factureUrl && proformats.filter(p => p.fichier_url).length === 0 && (
        <div style={{ color: C.gray, fontSize: 13, fontStyle: 'italic' }}>Aucun document joint</div>
      )}
    </div>
  );
}

// ── Historique ────────────────────────────────────────────────────────────────
function SectionHistorique({ historique }) {
  const [voirTout, setVoirTout] = useState(false);
  const lignes = voirTout ? historique : historique.slice(-3).reverse();
  const total = historique.length;

  if (total === 0) {
    return <div style={{ color: C.gray, fontSize: 13, fontStyle: 'italic' }}>Aucun historique</div>;
  }
  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {lignes.map((h, i) => (
          <div key={h.id || i} style={{ fontSize: 13, color: '#2c3e50', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ color: C.gray }}>•</span>
            <span>
              <strong>{h.statut_avant && h.statut_apres ? `${h.statut_avant} → ${h.statut_apres}` : h.statut_apres || 'Action'}</strong>
              {h.utilisateur_nom && <span style={{ color: C.gray }}> — {h.utilisateur_nom}</span>}
              {h.commentaire && <span style={{ color: C.gray, fontStyle: 'italic' }}> « {h.commentaire} »</span>}
              <span style={{ color: C.gray, fontSize: 11 }}> — {formatDateHeure(h.created_at)}</span>
            </span>
          </div>
        ))}
      </div>
      {total > 3 && !voirTout && (
        <button onClick={() => setVoirTout(true)}
          style={{ marginTop: 8, background: 'none', border: 'none', color: C.secondary,
            cursor: 'pointer', fontSize: 12, padding: 0 }}>
          Voir tout ({total} entrées) ›
        </button>
      )}
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function DossierDetailPage() {
  const { numero } = useParams();
  const navigate = useNavigate();
  const { utilisateur } = useAuth();
  // Stocke l'id numérique après le premier fetch (toutes les sous-routes l'utilisent)
  const dossierIdRef = React.useRef(null);

  const [dossier, setDossier] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionEnCours, setActionEnCours] = useState(false);
  // Seuil dynamique : lit nb_proformats_requis du dossier (défaut 3)
  const seuilProformats = dossier?.nb_proformats_requis || 3;
  const [showFormProformat, setShowFormProformat] = useState(false);
  const [formProformat, setFormProformat] = useState({ fournisseur_id: '', fournisseur_nom: '', numero_proformat: '', montant_ttc: '', date_proformat: '', fichier: null });
  const [fournisseurs, setFournisseurs] = useState([]);
  const [avisParProformat, setAvisParProformat] = useState({});
  const [avisControle, setAvisControle] = useState([]);
  const [commentaireControle, setCommentaireControle] = useState('');
  const [commentaires, setCommentaires] = useState([]);
  const [proformatEnEdition, setProformatEnEdition] = useState(null);
  const [modeEdition, setModeEdition] = useState(false);
  const [formEdition, setFormEdition] = useState({
    objet: '', type_dossier: 'ACHAT', urgent: false, justification: ''
  });
  const [savingEdition, setSavingEdition] = useState(false);
  // ===== STATES ARTICLES =====
  const [showFormArticle, setShowFormArticle] = useState(false);
  const [articleEnEdition, setArticleEnEdition] = useState(null);
  const [savingArticle, setSavingArticle] = useState(false);
  const [formArticle, setFormArticle] = useState({
    designation: '', quantite: 1, unite: 'unité',
    prix_unitaire_estime: '', observations: ''
  });
  // ===== FIN STATES ARTICLES =====
  const [modePaiement, setModePaiement] = useState(null); // null | 'especes' | 'cheque'
  const [savingPaiement, setSavingPaiement] = useState(false);
  const [formPaiementEspeces, setFormPaiementEspeces] = useState({ numero_recu: '', recu: null });
  const [formPaiementCheque, setFormPaiementCheque] = useState({ numero_cheque: '', banque_cheque: '', date_cheque: '', scan_cheque: null });
  // ===== FIN STATES PAIEMENT =====
  const [erreurBordereau, setErreurBordereau] = useState('');
  // ===== STATES MODAL RECEPTION FACTURE =====
  const [showModalFacture, setShowModalFacture] = useState(false);
  const [formFacture, setFormFacture] = useState({ fichier: null, numero_facture: '', montant_ttc: '', commentaire: '' });
  const [erreurFacture, setErreurFacture] = useState('');
  const [savingFacture, setSavingFacture] = useState(false);
  // ===== FIN STATES MODAL RECEPTION FACTURE =====
  const [showModalCloture, setShowModalCloture] = useState(false);
  const [commentaireCloture, setCommentaireCloture] = useState('');
  const [savingCloture, setSavingCloture] = useState(false);
  const [showModalRapport, setShowModalRapport] = useState(false);
  const [rapportData, setRapportData] = useState(null);
  const [loadingRapport, setLoadingRapport] = useState(false);
  const [telechargementZip, setTelechargementZip] = useState(false);
  // ===== FIN STATES CLOTURE =====
  useEffect(() => {
    if (dossier?.mode_paiement) {
      setModePaiement(dossier.mode_paiement);
    }
  }, [dossier]);
  const monRole = utilisateur?.role || '';
  const monId = utilisateur?.id;
  const estDemandeur = dossier && (
    String(dossier.demandeur_id) === String(monId) ||
    String(dossier.createur_id) === String(monId) ||
    String(dossier.utilisateur_id) === String(monId)
  );
  const peutEditerBrouillon = dossier?.statut === 'BROUILLON' && (
    estDemandeur ||
    ['prepose', 'responsable_preposes', 'admin', 'super_admin'].includes(monRole)
  );
  const { peut } = usePermissions();
  const ROLES_FINANCIERS = ['admin', 'super_admin', 'service_achats', 'responsable_achats',
    'verificateur', 'controle_gestion', 'comptable', 'caissier', 'caissier_principal',
    'direction', 'responsable_preposes', 'comptabilite'];
  const voitMontants = ROLES_FINANCIERS.includes(monRole);
  const ROLES_VOIENT_PDF = ['super_admin', 'admin', 'service_achats', 'responsable_achats',
    'verificateur', 'controle_gestion', 'comptable', 'comptabilite', 'direction', 'caissier_principal'];
  const peutVoirProformatPDF = ROLES_VOIENT_PDF.includes(monRole);

  const charger = useCallback(async () => {
    try {
      // Premier chargement : résolution par numéro ; rechargements : id numérique connu
      const dRes = dossierIdRef.current
        ? await api.get(`/achats/dossiers/${dossierIdRef.current}`)
        : await api.get(`/achats/dossiers/by-numero/${encodeURIComponent(numero)}`);
      const dossierData = dRes.data;
      dossierIdRef.current = dossierData.id; // mémoriser l'id numérique

      const dosId = dossierData.id;
      const [lRes, fRes] = await Promise.all([
        api.get(`/achats/dossiers/${dosId}/lignes`).catch(() => ({ data: [] })),
        api.get('/achats/fournisseurs').catch(() => ({ data: [] })),
      ]);
      setLignes(lRes.data || []);
      setFournisseurs(fRes.data?.fournisseurs || fRes.data || []);

      // Mettre à jour le titre de la page
      document.title = `${dossierData.numero} — SOGAS Achats`;

      // Charger les avis de contrôle si statut EN_CONTROLE ou EN_COMPTABILITE ou retour depuis contrôle
      const historique = dossierData.historique || [];
      const retour = historique.filter(h =>
        (h.statut_avant === 'EN_CONTROLE' && h.statut_apres === 'EN_ACHATS') ||
        (h.statut_avant === 'EN_COMPTABILITE' && h.statut_apres === 'EN_CONTROLE')
      ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      const doitChargerAvis = retour || ['EN_CONTROLE', 'EN_COMPTABILITE'].includes(dossierData.statut);
      if (doitChargerAvis) {
        const proformats = dossierData.proformats || [];
        const avisRes = await Promise.all(
          proformats.map(p =>
            api.get(`/achats/dossiers/${dosId}/proformats/${p.id}/avis`)
              .then(r => r.data.map(a => ({
                ...a,
                fournisseur_nom: p.fournisseur_nom || p.fournisseur_nom_libre || p.numero_proformat,
              })))
              .catch(() => [])
          )
        );
        const tousAvis = avisRes.flat();
        setAvisControle(tousAvis);
        const parProformat = {};
        tousAvis.forEach(a => {
          if (!parProformat[a.proformat_id]) parProformat[a.proformat_id] = {};
          parProformat[a.proformat_id] = {
            avis: a.avis || '',
            commentaire: a.commentaire || '',
            proposition_alternative: a.proposition_alternative || '',
            showProposition: false,
          };
        });
        setAvisParProformat(prev => ({ ...prev, ...parProformat }));
      }
      setDossier(dossierData);
      // Extraire commentaires non vides depuis l'historique
      const comms = (dossierData.historique || []).filter(h => h.commentaire && h.commentaire.trim() !== '');
      setCommentaires(comms);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [numero]);

  useEffect(() => { charger(); }, [charger]);

  const executeAction = async (nouveau_statut, commentaireAction) => {
    if (!window.confirm('Confirmer cette action ?')) return;
    setActionEnCours(true);
    try {
      await api.post(`/achats/dossiers/${dossier.id}/transition`, {
        nouveau_statut,
        commentaire: commentaireAction || '',
      });
      const [dRes, lRes] = await Promise.all([
        api.get(`/achats/dossiers/${dossier.id}`),
        api.get(`/achats/dossiers/${dossier.id}/lignes`).catch(() => ({ data: [] })),
      ]);
      setDossier(dRes.data);
      setLignes(lRes.data || []);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la transition');
    } finally {
      setActionEnCours(false);
    }
  };

  const handleModifierProformat = (p) => {
    setProformatEnEdition({
      id: p.id,
      fournisseur_id: p.fournisseur_id || '',
      fournisseur_nom_libre: p.fournisseur_nom_libre || '',
      montant_ttc: p.montant_ttc || '',
      numero_proformat: p.numero_proformat || '',
      fichier: null,
    });
  };

  const handleSupprimerProformat = async (proformatId) => {
    if (!window.confirm('Supprimer ce proformat ?')) return;
    try {
      await api.delete(`/achats/proformats/${proformatId}`);
      charger();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const handleSauvegarderModification = async () => {
    try {
      const fd = new FormData();
      if (proformatEnEdition.fournisseur_id) fd.append('fournisseur_id', proformatEnEdition.fournisseur_id);
      if (proformatEnEdition.fournisseur_nom_libre) fd.append('fournisseur_nom_libre', proformatEnEdition.fournisseur_nom_libre);
      fd.append('montant_ttc', proformatEnEdition.montant_ttc);
      fd.append('numero_proformat', proformatEnEdition.numero_proformat);
      if (proformatEnEdition.fichier) fd.append('fichier', proformatEnEdition.fichier);
      await api.put(`/achats/proformats/${proformatEnEdition.id}`, fd);
      setProformatEnEdition(null);
      charger();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const btnStyle = (color) => ({
    background: color, color: 'white', border: 'none', borderRadius: '8px',
    padding: '10px 24px', fontSize: '14px', fontWeight: '600',
    cursor: actionEnCours ? 'not-allowed' : 'pointer', opacity: actionEnCours ? 0.7 : 1,
  });

  // ===== FONCTIONS ARTICLES =====
  const resetFormArticle = () => setFormArticle({
    designation: '', quantite: 1, unite: 'unité',
    prix_unitaire_estime: '', observations: ''
  });

  const handleAjouterArticle = async () => {
    if (!formArticle.designation.trim()) { alert('La désignation est obligatoire'); return; }
    setSavingArticle(true);
    try {
      await api.post(`/achats/dossiers/${dossier.id}/lignes`, {
        ...formArticle,
        prix_unitaire_estime: formArticle.prix_unitaire_estime || null
      });
      const [lRes, dRes] = await Promise.all([
        api.get(`/achats/dossiers/${dossier.id}/lignes`),
        api.get(`/achats/dossiers/${dossier.id}`)
      ]);
      setLignes(lRes.data || []);
      setDossier(dRes.data.dossier || dRes.data);
      setShowFormArticle(false);
      resetFormArticle();
    } catch(e) { alert(e.response?.data?.message || 'Erreur ajout article'); }
    finally { setSavingArticle(false); }
  };

  const handleModifierArticle = async () => {
    if (!articleEnEdition?.designation?.trim()) { alert('La désignation est obligatoire'); return; }
    setSavingArticle(true);
    try {
      await api.put(`/achats/dossiers/${dossier.id}/lignes/${articleEnEdition.id}`, articleEnEdition);
      const [lRes, dRes] = await Promise.all([
        api.get(`/achats/dossiers/${dossier.id}/lignes`),
        api.get(`/achats/dossiers/${dossier.id}`)
      ]);
      setLignes(lRes.data || []);
      setDossier(dRes.data.dossier || dRes.data);
      setArticleEnEdition(null);
    } catch(e) { alert(e.response?.data?.message || 'Erreur modification article'); }
    finally { setSavingArticle(false); }
  };

  const handleSupprimerArticle = async (ligneId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await api.delete(`/achats/dossiers/${dossier.id}/lignes/${ligneId}`);
      const [lRes, dRes] = await Promise.all([
        api.get(`/achats/dossiers/${dossier.id}/lignes`),
        api.get(`/achats/dossiers/${dossier.id}`)
      ]);
      setLignes(lRes.data || []);
      setDossier(dRes.data.dossier || dRes.data);
    } catch(e) { alert(e.response?.data?.message || 'Erreur suppression article'); }
  };

  const totalEstime = lignes.reduce((sum, l) =>
    sum + (parseFloat(l.quantite) * parseFloat(l.prix_unitaire_estime || 0)), 0
  );
  // ===== FIN FONCTIONS ARTICLES =====

  const handleChoisirModePaiement = async (mode) => {
    setSavingPaiement(true);
    try {
      await api.post(`/achats/dossiers/${dossier.id}/paiement/mode`, { mode_paiement: mode });
      setModePaiement(mode);
    } catch(e) {
      alert(e.response?.data?.message || 'Erreur lors du choix du mode');
    } finally {
      setSavingPaiement(false);
    }
  };

  const handlePaiementEspeces = async () => {
    if (!formPaiementEspeces.numero_recu) { alert('Le numéro de reçu est obligatoire'); return; }
    setSavingPaiement(true);
    try {
      const fd = new FormData();
      fd.append('numero_recu', formPaiementEspeces.numero_recu);
      if (formPaiementEspeces.recu) fd.append('recu', formPaiementEspeces.recu);
      await api.post(`/achats/dossiers/${dossier.id}/paiement/especes`, fd);
      await charger();
    } catch(e) {
      alert(e.response?.data?.message || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setSavingPaiement(false);
    }
  };

  const handlePaiementCheque = async () => {
    const { numero_cheque, banque_cheque, date_cheque } = formPaiementCheque;
    if (!numero_cheque || !banque_cheque || !date_cheque) {
      alert('Numéro de chèque, banque et date sont obligatoires');
      return;
    }
    setSavingPaiement(true);
    try {
      const fd = new FormData();
      fd.append('numero_cheque', numero_cheque);
      fd.append('banque_cheque', banque_cheque);
      fd.append('date_cheque', date_cheque);
      if (formPaiementCheque.scan_cheque) fd.append('recu', formPaiementCheque.scan_cheque);
      await api.post(`/achats/dossiers/${dossier.id}/paiement/cheque`, fd);
      await charger();
    } catch(e) {
      alert(e.response?.data?.message || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setSavingPaiement(false);
    }
  };
  // ===== FIN FONCTIONS PAIEMENT =====

  const handleCloturer = async () => {
    if (!commentaireCloture.trim()) { alert('Le commentaire de clôture est obligatoire'); return; }
    setSavingCloture(true);
    try {
      await api.post(`/achats/dossiers/${dossier.id}/cloturer`, { commentaire_cloture: commentaireCloture.trim() });
      setShowModalCloture(false);
      setCommentaireCloture('');
      await charger();
    } catch(e) {
      alert(e.response?.data?.message || 'Erreur lors de la clôture');
    } finally {
      setSavingCloture(false);
    }
  };

  const handleVoirRapport = async () => {
    setLoadingRapport(true);
    setShowModalRapport(true);
    try {
      const res = await api.get(`/achats/dossiers/${dossier.id}/rapport`);
      setRapportData(res.data);
    } catch(e) {
      alert(e.response?.data?.message || 'Erreur lors du chargement du rapport');
      setShowModalRapport(false);
    } finally {
      setLoadingRapport(false);
    }
  };

  const handleTelechargerZip = async () => {
    setTelechargementZip(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`/api/achats/dossiers/${dossier.id}/telecharger-zip`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur serveur');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dossier-${dossier.numero}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) {
      alert('Erreur lors du téléchargement : ' + e.message);
    } finally {
      setTelechargementZip(false);
    }
  };
  // ===== FIN FONCTIONS CLOTURE =====

  const ouvrirEdition = () => {
    setFormEdition({
      objet: dossier.objet || '',
      type_dossier: dossier.type_dossier || 'ACHAT',
      urgent: dossier.urgence || false,
      justification: dossier.justification_urgence || ''
    });
    setModeEdition(true);
  };

  const sauvegarderEdition = async () => {
    if (!formEdition.objet.trim()) {
      alert('L\'objet est obligatoire');
      return;
    }
    setSavingEdition(true);
    try {
      const res = await api.put(`/achats/dossiers/${dossier.id}/brouillon`, formEdition);
      setDossier(res.data.dossier || res.data);
      setModeEdition(false);
    } catch(e) {
      alert(e.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingEdition(false);
    }
  };

  const supprimerDossier = async () => {
    if (!window.confirm('Supprimer définitivement ce dossier ?\nCette action est irréversible.')) return;
    try {
      await api.delete(`/achats/dossiers/${dossier.id}/brouillon`);
      navigate('/achats');
    } catch(e) {
      alert(e.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const getBoutonAction = () => {
    if (!dossier) return null;
    const s = dossier.statut;
    const r = monRole;
    const peutEcrire   = peut('achats', 'ecrire');
    const peutApprouver = peut('achats', 'approuver');
    if (!peutEcrire && !peutApprouver) return <span style={{ color: '#999', fontSize: 13, fontStyle: 'italic' }}>Aucune action disponible pour votre rôle à cette étape</span>;

    if (s === 'BROUILLON' && ['prepose', 'responsable_preposes', 'admin', 'super_admin'].includes(r))
      return <button onClick={() => executeAction('SOUMIS')} style={btnStyle('#1976d2')}>📤 Soumettre le dossier</button>;

    if (s === 'SOUMIS' && dossier.type_dossier === 'TRAVAUX' && ['service_achats', 'responsable_achats', 'responsable_preposes', 'admin', 'super_admin'].includes(r))
      return <button onClick={() => executeAction('EN_MAINTENANCE')} style={btnStyle('#7b1fa2')}>🔧 Envoyer en maintenance</button>;

    if (s === 'SOUMIS' && ['service_achats', 'responsable_achats', 'responsable_preposes', 'admin', 'super_admin'].includes(r))
      return <button onClick={() => executeAction('EN_ACHATS')} style={btnStyle('#f57c00')}>🛒 Traiter en achats</button>;

    if (s === 'EN_MAINTENANCE' && ['maintenance', 'admin', 'super_admin'].includes(r))
      return <button onClick={() => executeAction('EN_ACHATS')} style={btnStyle('#388e3c')}>✅ Valider la maintenance</button>;

    if (s === 'EN_ACHATS' && ['service_achats', 'responsable_achats', 'admin', 'super_admin'].includes(r)) {
      const proformats = dossier.proformats || [];
      const proformatRetenu = proformats.some(p => p.selectionne === true);
      const peutEnvoyer = dossier.urgence === true || (proformats.length >= seuilProformats && proformatRetenu);
      if (!peutEnvoyer) {
        const raison = proformats.length < seuilProformats
          ? `${seuilProformats - proformats.length} proformat(s) manquant(s)`
          : 'Aucun proformat sélectionné';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <button disabled title={raison}
              style={{ ...btnStyle('#1976d2'), opacity: 0.45, cursor: 'not-allowed' }}>
              🔍 Envoyer en contrôle — {raison}
            </button>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          {dossier.urgence && (
            <span style={{ fontSize: 12, color: '#e65100', fontWeight: 600 }}>
              ⚡ Mode urgence actif — proformats non requis
            </span>
          )}
          <button onClick={() => executeAction('EN_CONTROLE')} style={btnStyle('#1976d2')}>🔍 Envoyer en contrôle</button>
          {dossier.urgence && (
            <small style={{ color: '#e65100', fontSize: 11 }}>⚡ Envoi sans proformats autorisé (dossier urgent)</small>
          )}
        </div>
      );
    }

    if (s === 'EN_CONTROLE') {
      const rolesControle = ['verificateur', 'controle_gestion', 'admin', 'super_admin'];
      if (rolesControle.includes(r) && peutApprouver)
        return (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button onClick={() => executeAction('EN_COMPTABILITE', commentaireControle)} style={btnStyle('#388e3c')}>
              ✅ Valider le contrôle
            </button>
            <button onClick={() => {
              const motif = window.prompt('Motif du retour en Achats :');
              if (motif && motif.trim()) executeAction('EN_ACHATS', motif.trim());
            }} style={btnStyle('#f57c00')}>
              ↩ Retourner en Achats
            </button>
          </div>
        );
      if (['comptable', 'comptabilite'].includes(r))
        return (
          <div style={{ color: '#1565c0', background: '#e3f2fd', border: '1px solid #90caf9',
            borderRadius: 8, padding: '10px 20px', fontSize: 13 }}>
            👁️ Dossier en cours de contrôle — consultation uniquement
          </div>
        );
    }

    if (s === 'EN_COMPTABILITE') {
      const rolesComptabilite = ['comptable', 'comptabilite', 'direction', 'admin', 'super_admin'];
      if (rolesComptabilite.includes(r) && peutApprouver)
        return (
          <div>
            {/* Récapitulatif pour la Direction */}
            {r === 'direction' && (
              <div style={{ background: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: '#6a1b9a', marginBottom: 12, fontSize: 14 }}>
                  📊 Récapitulatif des avis avant approbation
                </div>
                {(dossier.proformats || []).filter(p => p.selectionne).map(p => (
                  <div key={p.id} style={{ background: 'white', borderRadius: 6, padding: '10px 14px', marginBottom: 10, border: '2px solid #4caf50' }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                      ✅ Choix du Service Achats : {p.fournisseur_nom || p.fournisseur_nom_libre || p.numero_proformat}
                    </div>
                    <div style={{ color: '#555', fontSize: 12 }}>
                      Montant : {p.montant_ttc ? Number(p.montant_ttc).toLocaleString('fr-FR') : '—'} FCFA
                    </div>
                  </div>
                ))}
                {(avisControle || []).filter(a => a.proposition_alternative).map((a, i) => (
                  <div key={i} style={{ background: '#e8f5e9', borderRadius: 6, padding: '10px 14px', marginBottom: 8, border: '1px solid #a5d6a7' }}>
                    <div style={{ fontWeight: 600, color: '#2e7d32', fontSize: 12, marginBottom: 4 }}>
                      💡 Suggestion de {a.auteur_nom} :
                    </div>
                    <div style={{ color: '#444', fontSize: 12, fontStyle: 'italic' }}>
                      "{a.proposition_alternative}"
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => executeAction('APPROUVE', commentaireControle)} style={btnStyle('#388e3c')}>
              ✅ Valider & Approuver
            </button>
            <button onClick={() => {
              const motif = window.prompt('Motif du rejet (obligatoire) :');
              if (motif && motif.trim()) executeAction('REJETE', motif.trim());
            }} style={btnStyle('#d32f2f')}>
              ❌ Rejeter le dossier
            </button>
            <button onClick={() => {
              const motif = window.prompt('Motif du retour au Contrôle :');
              if (motif && motif.trim()) executeAction('EN_CONTROLE', motif.trim());
            }} style={btnStyle('#f57c00')}>
              ↩ Retourner au Contrôle
            </button>
            </div>
          </div>
        );
      if (['verificateur', 'controle_gestion'].includes(r))
        return (
          <div style={{ color: '#1565c0', background: '#e3f2fd', border: '1px solid #90caf9',
            borderRadius: 8, padding: '10px 20px', fontSize: 13 }}>
            👁️ Dossier en cours de traitement par la Comptabilité — consultation uniquement
          </div>
        );
    }

    if (s === 'APPROUVE' && ['service_achats', 'responsable_achats', 'admin', 'super_admin'].includes(r))
      return <button onClick={() => executeAction('EN_EXECUTION')} style={btnStyle('#f57c00')}>🚀 Mettre en exécution</button>;

    if (s === 'EN_EXECUTION') {
      const estDemandeur = dossier.demandeur_id === monId;
      const estMemeAbattoirPrepose = ['prepose','responsable_preposes'].includes(r) && dossier.abattoir_id === utilisateur?.abattoir_id;
      const peutConfirmerLivraison = estDemandeur || estMemeAbattoirPrepose || ['admin','super_admin'].includes(r);
      const bordereauOk = !!dossier.bordereau_livraison_url;

      if (peutConfirmerLivraison) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end' }}>
            {/* Message erreur bordereau manquant */}
            {erreurBordereau && (
              <div style={{
                background: '#FFEBEE', border: '1px solid #FFCDD2',
                borderRadius: '8px', padding: '10px 16px',
                fontSize: '13px', color: '#C62828', width: '100%',
              }}>
                ⚠ {erreurBordereau}
              </div>
            )}
            {!bordereauOk && !erreurBordereau && (
              <div style={{
                background: '#fff3e0', border: '1px solid #ff9800',
                borderRadius: '8px', padding: '10px 16px',
                fontSize: '13px', color: '#e65100',
              }}>
                ⚠️ Uploadez le bordereau de livraison avant de confirmer
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  if (!bordereauOk) {
                    setErreurBordereau('Le bordereau de livraison doit être joint avant de confirmer la livraison. Veuillez uploader le document dans la section Documents.');
                    return;
                  }
                  setErreurBordereau('');
                  executeAction('LIVRE', 'Livraison confirmée par le demandeur');
                }}
                style={{ ...btnStyle(bordereauOk ? '#388e3c' : '#bdbdbd'), cursor: bordereauOk ? 'pointer' : 'not-allowed' }}
              >
                📦 Confirmer la réception
              </button>
              {!bordereauOk && (
                <label style={{
                  background: '#1976d2', color: 'white', border: 'none',
                  borderRadius: '8px', padding: '10px 20px', cursor: 'pointer',
                  fontSize: '14px', fontWeight: '600',
                  display: 'inline-flex', alignItems: 'center', gap: '6px'
                }}>
                  📎 Uploader bordereau
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const fd = new FormData();
                      fd.append('bordereau', e.target.files[0]);
                      try {
                        await api.post(`/achats/dossiers/${dossier.id}/bordereau`, fd);
                        setErreurBordereau('');
                        const res = await api.get(`/achats/dossiers/${dossier.id}`);
                        setDossier(res.data.dossier || res.data);
                      } catch(err) {
                        setErreurBordereau('Erreur upload : ' + (err.response?.data?.message || err.message));
                      }
                    }}
                  />
                </label>
              )}
            </div>
          </div>
        );
      }

      return (
        <div style={{
          color: '#1565c0', background: '#e3f2fd',
          border: '1px solid #90caf9', borderRadius: '8px',
          padding: '10px 20px', fontSize: '13px'
        }}>
          📦 En attente de confirmation de livraison par le demandeur
        </div>
      );
    }

    if (s === 'LIVRE') {
      const rolesFacture = ['service_achats','responsable_achats','comptable','comptabilite','admin','super_admin'];
      const estDemandeur = dossier.demandeur_id === monId;
      const estMemeAbattoirPrepose = ['prepose','responsable_preposes'].includes(r) && dossier.abattoir_id === utilisateur?.abattoir_id;
      const peutConfirmerLivraison = estDemandeur || estMemeAbattoirPrepose || ['admin','super_admin'].includes(r);

      if (rolesFacture.includes(r) && peutApprouver) {
        return (
          <button
            onClick={() => { setFormFacture({ fichier: null, numero_facture: '', montant_ttc: '', commentaire: '' }); setErreurFacture(''); setShowModalFacture(true); }}
            style={btnStyle('#7b1fa2')}
          >
            🧾 Enregistrer réception facture
          </button>
        );
      }

      if (peutConfirmerLivraison) {
        return (
          <div style={{
            color: '#4caf50', background: '#e8f5e9',
            border: '1px solid #a5d6a7', borderRadius: '8px',
            padding: '10px 20px', fontSize: '13px', fontWeight: '600'
          }}>
            ✅ Livraison confirmée — En attente de réception de la facture fournisseur
          </div>
        );
      }

      return (
        <div style={{
          color: '#666', background: '#f5f5f5',
          borderRadius: '8px', padding: '10px 20px', fontSize: '13px'
        }}>
          📋 Dossier livré — traitement en cours
        </div>
      );
    }

    if (s === 'FACTURE_RECUE') {
      const rolesComptable = ['comptable','comptabilite','admin','super_admin'];
      const rolesCaissier = ['caissier_principal','admin','super_admin'];
      const modeActuel = modePaiement || dossier.mode_paiement;
      const proformatsFR = dossier.proformats || [];

      if (!modeActuel && rolesComptable.includes(monRole)) {
        return (
          <div style={{background:'white', border:'1px solid #e0e0e0', borderRadius:'8px', padding:'16px 20px', display:'flex', flexDirection:'column', gap:'12px'}}>
            <div style={{fontWeight:'600', fontSize:'14px', color:'#333'}}>💰 Choisir le mode de paiement</div>
            <div style={{fontSize:'12px', color:'#666', fontStyle:'italic'}}>
              Montant : {dossier.montant_estime?.toLocaleString('fr-FR')} FCFA
              · Fournisseur : {proformatsFR.find(p => p.selectionne)?.fournisseur_nom || '—'}
            </div>
            <div style={{display:'flex', gap:'12px'}}>
              <button onClick={() => handleChoisirModePaiement('especes')} style={{flex:1, padding:'14px', borderRadius:'8px', border:'2px solid #e0e0e0', background:'white', cursor:'pointer', fontSize:'14px', fontWeight:'600', display:'flex', flexDirection:'column', alignItems:'center', gap:'6px'}}>
                <span style={{fontSize:'28px'}}>💵</span>
                <span>Espèces</span>
                <span style={{fontSize:'11px', color:'#888', fontWeight:'400'}}>Caissier Principal</span>
              </button>
              <button onClick={() => handleChoisirModePaiement('cheque')} style={{flex:1, padding:'14px', borderRadius:'8px', border:'2px solid #e0e0e0', background:'white', cursor:'pointer', fontSize:'14px', fontWeight:'600', display:'flex', flexDirection:'column', alignItems:'center', gap:'6px'}}>
                <span style={{fontSize:'28px'}}>🏦</span>
                <span>Chèque</span>
                <span style={{fontSize:'11px', color:'#888', fontWeight:'400'}}>Comptable</span>
              </button>
            </div>
          </div>
        );
      }

      if (modeActuel === 'especes' && rolesCaissier.includes(monRole)) {
        return (
          <div style={{background:'#fff8e1', border:'1px solid #ffc107', borderRadius:'8px', padding:'16px 20px'}}>
            <div style={{fontWeight:'700', fontSize:'14px', color:'#f57f17', marginBottom:'12px'}}>💵 Paiement en espèces</div>
            <div style={{fontSize:'12px', color:'#666', marginBottom:'12px'}}>
              Montant : <strong>{dossier.montant_estime?.toLocaleString('fr-FR')} FCFA</strong>
              · Bénéficiaire : <strong>{proformatsFR.find(p => p.selectionne)?.fournisseur_nom || '—'}</strong>
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px'}}>
              <div>
                <label style={{fontSize:'12px', fontWeight:'600', display:'block', marginBottom:'4px'}}>N° Reçu (optionnel)</label>
                <input type="text" value={formPaiementEspeces.numero_recu} onChange={e => setFormPaiementEspeces({...formPaiementEspeces, numero_recu: e.target.value})} placeholder="Ex: RECU-2026-001" style={{width:'100%', padding:'8px', borderRadius:'6px', border:'1px solid #ddd', fontSize:'13px', boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:'12px', fontWeight:'600', display:'block', marginBottom:'4px'}}>Photo reçu signé (optionnel)</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFormPaiementEspeces({...formPaiementEspeces, recu: e.target.files[0]})} style={{fontSize:'12px', width:'100%'}}/>
              </div>
            </div>
            <button onClick={handlePaiementEspeces} disabled={savingPaiement} style={{background:'#f57c00', color:'white', border:'none', borderRadius:'8px', padding:'10px 24px', cursor: savingPaiement ? 'not-allowed' : 'pointer', fontSize:'14px', fontWeight:'600', opacity: savingPaiement ? 0.7 : 1}}>
              {savingPaiement ? '⏳ En cours...' : '💵 Confirmer paiement espèces'}
            </button>
          </div>
        );
      }

      if (modeActuel === 'especes' && rolesComptable.includes(monRole)) {
        return (
          <div style={{background:'#fff8e1', border:'1px solid #ffc107', borderRadius:'8px', padding:'12px 20px', color:'#f57f17', fontSize:'13px'}}>
            💵 Mode espèces défini — En attente du Caissier Principal
          </div>
        );
      }

      if (modeActuel === 'cheque' && rolesComptable.includes(monRole)) {
        return (
          <div style={{background:'#e3f2fd', border:'1px solid #90caf9', borderRadius:'8px', padding:'16px 20px'}}>
            <div style={{fontWeight:'700', fontSize:'14px', color:'#1565c0', marginBottom:'12px'}}>🏦 Paiement par chèque</div>
            <div style={{fontSize:'12px', color:'#666', marginBottom:'12px'}}>
              Montant : <strong>{dossier.montant_estime?.toLocaleString('fr-FR')} FCFA</strong>
              · Bénéficiaire : <strong>{proformatsFR.find(p => p.selectionne)?.fournisseur_nom || '—'}</strong>
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'12px'}}>
              <div>
                <label style={{fontSize:'12px', fontWeight:'600', display:'block', marginBottom:'4px'}}>N° Chèque *</label>
                <input type="text" value={formPaiementCheque.numero_cheque} onChange={e => setFormPaiementCheque({...formPaiementCheque, numero_cheque: e.target.value})} placeholder="Ex: 0012345" style={{width:'100%', padding:'8px', borderRadius:'6px', border:'1px solid #90caf9', fontSize:'13px', boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:'12px', fontWeight:'600', display:'block', marginBottom:'4px'}}>Banque *</label>
                <select value={formPaiementCheque.banque_cheque} onChange={e => setFormPaiementCheque({...formPaiementCheque, banque_cheque: e.target.value})} style={{width:'100%', padding:'8px', borderRadius:'6px', border:'1px solid #90caf9', fontSize:'13px'}}>
                  <option value="">-- Choisir --</option>
                  <option value="CBAO">CBAO</option>
                  <option value="SGBS">SGBS</option>
                  <option value="BHS">BHS</option>
                  <option value="BICIS">BICIS</option>
                  <option value="UBA">UBA</option>
                  <option value="Ecobank">Ecobank</option>
                  <option value="BOA">BOA</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <label style={{fontSize:'12px', fontWeight:'600', display:'block', marginBottom:'4px'}}>Date chèque *</label>
                <input type="date" value={formPaiementCheque.date_cheque} onChange={e => setFormPaiementCheque({...formPaiementCheque, date_cheque: e.target.value})} style={{width:'100%', padding:'8px', borderRadius:'6px', border:'1px solid #90caf9', fontSize:'13px'}}/>
              </div>
            </div>
            <div style={{marginBottom:'12px'}}>
              <label style={{fontSize:'12px', fontWeight:'600', display:'block', marginBottom:'4px'}}>Scan chèque (optionnel)</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFormPaiementCheque({...formPaiementCheque, scan_cheque: e.target.files[0]})} style={{fontSize:'12px'}}/>
            </div>
            <button onClick={handlePaiementCheque} disabled={savingPaiement} style={{background:'#1565c0', color:'white', border:'none', borderRadius:'8px', padding:'10px 24px', cursor: savingPaiement ? 'not-allowed' : 'pointer', fontSize:'14px', fontWeight:'600', opacity: savingPaiement ? 0.7 : 1}}>
              {savingPaiement ? '⏳ En cours...' : '🏦 Confirmer émission chèque'}
            </button>
          </div>
        );
      }

      return (
        <div style={{color:'#666', background:'#f5f5f5', borderRadius:'8px', padding:'10px 20px', fontSize:'13px'}}>
          💰 En attente de traitement du paiement
          {modeActuel && <span style={{marginLeft:'8px', fontWeight:'600'}}>— Mode : {modeActuel === 'especes' ? '💵 Espèces' : '🏦 Chèque'}</span>}
        </div>
      );
    }

    if (s === 'PAYE' && ['direction','admin', 'super_admin'].includes(r))
      return <button onClick={() => setShowModalCloture(true)} style={btnStyle('#455a64')}>🔒 Clôturer le dossier</button>;

    if (s === 'REJETE' && ['prepose', 'responsable_preposes', 'admin', 'super_admin'].includes(r))
      return <button onClick={() => executeAction('BROUILLON')} style={btnStyle('#f57c00')}>🔄 Remettre en brouillon</button>;

    return <span style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>Aucune action disponible pour votre rôle à cette étape</span>;
  };

  if (loading) return (
    <div style={{ background: C.light, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.gray, fontSize: 15 }}>Chargement du dossier…</div>
    </div>
  );
  if (error || !dossier) return (
    <div style={{ background: C.light, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.danger, fontSize: 15 }}>{error || 'Dossier introuvable'}</div>
    </div>
  );

  const historique = dossier.historique || [];

  // Dernier retour VERS le statut actuel (avec commentaire)
  const retourVersStatutActuel = historique
    .filter(h =>
      h.statut_apres === dossier.statut &&
      h.statut_avant !== 'BROUILLON' &&
      h.commentaire && h.commentaire.trim() !== ''
    )
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const dernierRetour = retourVersStatutActuel[0] || null;

  // C'est un retour seulement si le dossier a déjà dépassé ce statut auparavant
  const estUnRetour = dernierRetour && historique.some(h =>
    h.statut_avant === dossier.statut &&
    new Date(h.created_at) < new Date(dernierRetour.created_at)
  );

  const getTitreRetour = (retour) => {
    if (!retour) return null;
    const titres = {
      'EN_COMPTABILITE': '⚠️ Retourné par la Comptabilité',
      'EN_CONTROLE':     '⚠️ Retourné par le Contrôle de gestion',
      'EN_ACHATS':       '⚠️ Retourné par le Service Achats',
      'EN_VERIFICATION': '⚠️ Retourné par le Vérificateur',
      'APPROUVE':        '⚠️ Retourné depuis Approbation',
    };
    return titres[retour.statut_avant] || `⚠️ Retourné par ${retour.utilisateur_nom || 'un intervenant'}`;
  };

  // Commentaires à afficher dans Suivi (exclure ce qui est dans le bandeau)
  const idsDejaAffiches = dernierRetour && dernierRetour.id ? [dernierRetour.id] : [];
  const commentairesAffiches = historique
    .filter(h => h.commentaire && h.commentaire.trim() !== '' && !idsDejaAffiches.includes(h.id))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const getStyleCommentaire = (h) => {
    if (['EN_ACHATS', 'EN_CONTROLE', 'EN_MAINTENANCE'].includes(h.statut_apres) &&
        ['EN_CONTROLE', 'EN_COMPTABILITE', 'EN_ACHATS'].includes(h.statut_avant)) {
      return { borderLeft: '4px solid #ff9800', background: '#fff8e1' };
    }
    if (h.statut_apres === 'REJETE') {
      return { borderLeft: '4px solid #f44336', background: '#ffebee' };
    }
    if (['EN_CONTROLE', 'EN_COMPTABILITE', 'APPROUVE', 'CLOTURE'].includes(h.statut_apres)) {
      return { borderLeft: '4px solid #4caf50', background: '#f1f8e9' };
    }
    return { borderLeft: '4px solid #e0e0e0', background: '#f5f5f5' };
  };

  return (
    <div style={{ background: C.light, minHeight: '100vh', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", paddingBottom: 80 }}>

      {/* ── TOPBAR ─────────────────────────────────────────────────────── */}
      <div style={{
        background: C.white, borderBottom: `1px solid ${C.border}`,
        padding: '0 24px', height: 52, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            onClick={() => navigate('/achats')}
            style={{ color: C.primary, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
          >Achats</span>
          <span style={{ color: C.grayLight, fontSize: 13 }}>›</span>
          <span
            onClick={() => navigate('/achats')}
            style={{ color: C.primary, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
          >Dossiers</span>
          <span style={{ color: C.grayLight, fontSize: 13 }}>›</span>
          <span style={{ color: C.textDark, fontSize: 13, fontWeight: 700 }}>{dossier.numero}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => navigate('/achats')}
            style={{
              background: C.white, color: C.textMid, border: `1px solid ${C.border}`,
              borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13,
            }}
          >← Retour</button>
        </div>
      </div>

      {/* ── EN-TÊTE DOSSIER ────────────────────────────────────────────── */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: '20px 24px' }}>

        {/* Ligne 1 : numéro + badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={{ fontWeight: 800, fontSize: 22, color: C.primaryDark, fontFamily: 'monospace' }}>
            {dossier.numero}
          </span>

          <span style={{
            background: dossier.type_dossier === 'TRAVAUX' ? '#FFF3E0' : '#E3F2FD',
            color:      dossier.type_dossier === 'TRAVAUX' ? '#E65100' : '#1565C0',
            borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600,
          }}>{dossier.type_dossier}</span>

          {dossier.urgence && (
            <span style={{ background: '#FFEBEE', color: '#B71C1C',
              borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>⚡ URGENT</span>
          )}

          {dossier.validation_direction_requise && (
            <span style={{ background: '#FFF3E0', color: '#E65100',
              borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700, border: '1px solid #FFCC80' }}>
              🏛 Validation direction obligatoire
            </span>
          )}

          <StatutBadge statut={dossier.statut} />

          {voitMontants && dossier.montant_estime > 0 && (
            <span style={{ background: C.green100, color: C.primary,
              padding: '3px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
              💰 {Number(dossier.montant_estime).toLocaleString('fr-FR')} FCFA
            </span>
          )}
        </div>

        {/* Ligne 2 : objet + méta */}
        <div style={{ marginBottom: 16 }}>
          {dossier.objet && (
            <div style={{ fontSize: 16, fontWeight: 600, color: C.textDark, marginBottom: 4 }}>
              {dossier.objet}
            </div>
          )}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', color: C.textMid, fontSize: 13 }}>
            {dossier.abattoir_nom && <span>🏭 {dossier.abattoir_nom}</span>}
            {dossier.demandeur_nom && <span>👤 {dossier.demandeur_nom}</span>}
            <span>📅 {formatDate(dossier.created_at)}</span>
          </div>
        </div>

        {/* Stepper horizontal */}
        {(() => {
          const estTravaux = dossier.type_dossier === 'TRAVAUX';
          const wf = WORKFLOW_ORDRE.filter(s => s !== 'EN_MAINTENANCE' || estTravaux);
          const historique = dossier.historique || [];
          const statutsPassés = new Set(historique.map(h => h.statut_apres));
          const idxActuel = wf.indexOf(dossier.statut);

          return (
            <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', gap: 0 }}>
              {wf.map((s, i) => {
                const cfg = STATUTS_CONFIG[s] || { label: s, color: C.gray, bg: '#f5f5f5' };
                const actuel = s === dossier.statut;
                const passe = i < idxActuel || statutsPassés.has(s);
                return (
                  <React.Fragment key={s}>
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      minWidth: 72, flex: '0 0 auto',
                    }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%',
                        background: actuel ? cfg.bg : passe ? C.green100 : '#F5F5F5',
                        border: actuel ? `2px solid ${cfg.color}` : passe ? `2px solid ${C.primary}` : `2px solid #BDBDBD`,
                        color: actuel ? cfg.color : passe ? C.primary : '#BDBDBD',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700,
                        boxShadow: actuel ? `0 0 0 3px ${cfg.bg}` : 'none',
                        transition: 'all 0.2s',
                      }}>
                        {actuel ? '⏳' : passe ? '✓' : (i + 1)}
                      </div>
                      <div style={{
                        fontSize: 9, marginTop: 4, textAlign: 'center',
                        color: actuel ? cfg.color : passe ? C.primary : '#BDBDBD',
                        fontWeight: actuel ? 700 : passe ? 600 : 400,
                        lineHeight: 1.2, maxWidth: 68,
                      }}>{cfg.label}</div>
                    </div>
                    {i < wf.length - 1 && (
                      <div style={{
                        flex: 1, height: 2, minWidth: 8,
                        background: passe && i < idxActuel ? C.primary : '#E0E0E0',
                        marginBottom: 18,
                      }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* ── Bandeau retour vers statut actuel ─────────────────────────── */}
      {dernierRetour && estUnRetour && (
        <div style={{ background: '#fff3e0', border: '2px solid #ff9800', borderRadius: 8,
          padding: '16px 20px', margin: '16px 24px 0' }}>
          <div style={{ fontWeight: 700, color: '#e65100', fontSize: 15, marginBottom: 4 }}>
            {getTitreRetour(dernierRetour)}
          </div>
          <div style={{ color: '#777', fontSize: 12, marginBottom: 10 }}>
            Le {new Date(dernierRetour.created_at).toLocaleDateString('fr-FR')}{' '}
            à {new Date(dernierRetour.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            {dernierRetour.utilisateur_nom && ` — par ${dernierRetour.utilisateur_nom}`}
          </div>
          <div style={{ background: '#fff', borderLeft: '4px solid #ff9800',
            padding: '10px 14px', borderRadius: 4, marginBottom: 10, fontStyle: 'italic', color: '#333' }}>
            "{dernierRetour.commentaire}"
          </div>

          {/* Avis par proformat */}
          {avisControle.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 600, color: '#555', fontSize: 12, marginBottom: 8 }}>DÉTAIL PAR PROFORMAT :</div>
              {avisControle.map(av => (
                <div key={av.proformat_id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '8px 12px', marginBottom: 6, background: '#fff', borderRadius: 6,
                  borderLeft: av.avis === 'conforme' ? '3px solid #4caf50'
                    : av.avis === 'non_conforme' ? '3px solid #f44336' : '3px solid #ff9800',
                }}>
                  <span style={{ fontSize: 16 }}>
                    {av.avis === 'conforme' ? '✅' : av.avis === 'non_conforme' ? '❌' : '⚠️'}
                  </span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {av.fournisseur_nom || `Proformat #${av.proformat_id}`}
                    </div>
                    <div style={{ color: '#666', fontSize: 12, fontStyle: 'italic' }}>
                      {av.avis === 'conforme' ? 'Conforme' : av.avis === 'non_conforme' ? 'Non conforme' : 'Réserve émise'}
                      {av.commentaire && ` — "${av.commentaire}"`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ padding: '8px 12px', background: '#fff8e1', borderRadius: 6, fontSize: 12, color: '#f57f17', fontWeight: 600 }}>
            🎯 Action requise :{' '}
            {dossier.statut === 'EN_ACHATS' && 'Compléter les proformats et renvoyer en contrôle'}
            {dossier.statut === 'EN_CONTROLE' && 'Vérifier et corriger les documents signalés'}
            {dossier.statut === 'EN_COMPTABILITE' && 'Vérifier la facturation et les documents'}
          </div>
        </div>
      )}

      {/* ── Corps principal ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 20, padding: '20px 24px', alignItems: 'flex-start' }}>

        {/* ── Colonne gauche (60%) ──────────────────────────────────────── */}
        <div style={{ flex: '0 0 60%', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* SECTION 2 : Articles */}
          <div style={{ background: C.white, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>

            {dossier.statut === 'BROUILLON' && peutEditerBrouillon ? (
              /* ── Version éditable (BROUILLON) ── */
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0, fontSize: 15, color: C.primary, fontWeight: 700 }}>
                    📦 Articles demandés
                    {lignes.length > 0 && (
                      <span style={{ marginLeft: 8, background: '#e3f2fd', color: '#1565c0', fontSize: 12, padding: '2px 8px', borderRadius: 10 }}>
                        {lignes.length} article{lignes.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </h3>
                  <button
                    onClick={() => { setShowFormArticle(true); resetFormArticle(); }}
                    style={{ background: '#1976d2', color: 'white', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                  >
                    + Ajouter un article
                  </button>
                </div>

                {/* Formulaire ajout */}
                {showFormArticle && (
                  <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid #ddd' }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: '#1976d2' }}>Nouvel article</div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Désignation *</label>
                      <input type="text" value={formArticle.designation}
                        onChange={e => setFormArticle({ ...formArticle, designation: e.target.value })}
                        placeholder="Ex: Ordinateur portable"
                        style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Quantité *</label>
                        <input type="number" min="1" value={formArticle.quantite}
                          onChange={e => setFormArticle({ ...formArticle, quantite: parseInt(e.target.value) || 1 })}
                          style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Unité</label>
                        <select value={formArticle.unite} onChange={e => setFormArticle({ ...formArticle, unite: e.target.value })}
                          style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
                          {['unité','kg','litre','mètre','sachet','carton','sac','rouleau','boîte'].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Prix unitaire (FCFA) — optionnel</label>
                        <input type="number" min="0" value={formArticle.prix_unitaire_estime}
                          onChange={e => setFormArticle({ ...formArticle, prix_unitaire_estime: e.target.value })}
                          placeholder="0"
                          style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Observations (optionnel)</label>
                      <input type="text" value={formArticle.observations}
                        onChange={e => setFormArticle({ ...formArticle, observations: e.target.value })}
                        placeholder="Ex: Marque préférée, spécifications..."
                        style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleAjouterArticle} disabled={savingArticle}
                        style={{ background: '#388e3c', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: savingArticle ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: savingArticle ? 0.7 : 1 }}>
                        {savingArticle ? '⏳...' : '✅ Ajouter'}
                      </button>
                      <button onClick={() => setShowFormArticle(false)}
                        style={{ background: '#757575', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {/* Tableau avec édition inline */}
                {lignes.length === 0 ? (
                  <div style={{ color: '#999', fontStyle: 'italic', fontSize: 13, textAlign: 'center', padding: 20 }}>
                    Aucun article ajouté — cliquez sur "+ Ajouter un article"
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#1a237e', color: 'white' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Désignation</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center' }}>Qté</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center' }}>Unité</th>
                        {voitMontants && <th style={{ padding: '8px 12px', textAlign: 'right' }}>Prix unit.</th>}
                        {voitMontants && <th style={{ padding: '8px 12px', textAlign: 'right' }}>Total</th>}
                        <th style={{ padding: '8px 12px', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lignes.map((l, i) => (
                        <React.Fragment key={l.id || i}>
                          <tr style={{ background: i % 2 === 0 ? '#fafafa' : 'white', borderBottom: '1px solid #e0e0e0' }}>
                            <td style={{ padding: '10px 12px' }}>
                              <div style={{ fontWeight: 500 }}>{l.designation}</div>
                              {l.observations && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{l.observations}</div>}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>{l.quantite}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'center', color: '#666' }}>{l.unite}</td>
                            {voitMontants && (
                              <td style={{ padding: '10px 12px', textAlign: 'right', color: '#555' }}>
                                {l.prix_unitaire_estime ? Number(l.prix_unitaire_estime).toLocaleString('fr-FR') + ' FCFA' : <span style={{ color: '#bbb', fontStyle: 'italic' }}>À définir</span>}
                              </td>
                            )}
                            {voitMontants && (
                              <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#1a237e' }}>
                                {l.prix_unitaire_estime ? (parseFloat(l.quantite) * parseFloat(l.prix_unitaire_estime)).toLocaleString('fr-FR') + ' FCFA' : '—'}
                              </td>
                            )}
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                <button onClick={() => setArticleEnEdition({ ...l })}
                                  style={{ background: '#1976d2', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                                <button onClick={() => handleSupprimerArticle(l.id)}
                                  style={{ background: '#d32f2f', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                              </div>
                            </td>
                          </tr>
                          {articleEnEdition?.id === l.id && (
                            <tr>
                              <td colSpan={voitMontants ? 6 : 4} style={{ padding: 12, background: '#fffde7', borderBottom: '2px solid #fbc02d' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                                  <input type="text" value={articleEnEdition.designation}
                                    onChange={e => setArticleEnEdition({ ...articleEnEdition, designation: e.target.value })}
                                    placeholder="Désignation *"
                                    style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
                                  <input type="number" min="1" value={articleEnEdition.quantite}
                                    onChange={e => setArticleEnEdition({ ...articleEnEdition, quantite: parseInt(e.target.value) || 1 })}
                                    style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
                                  <select value={articleEnEdition.unite} onChange={e => setArticleEnEdition({ ...articleEnEdition, unite: e.target.value })}
                                    style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
                                    {['unité','kg','litre','mètre','sachet','carton','sac','rouleau','boîte'].map(u => <option key={u} value={u}>{u}</option>)}
                                  </select>
                                  <input type="number" min="0" value={articleEnEdition.prix_unitaire_estime || ''}
                                    onChange={e => setArticleEnEdition({ ...articleEnEdition, prix_unitaire_estime: e.target.value })}
                                    placeholder="Prix unit. (optionnel)"
                                    style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
                                </div>
                                <input type="text" value={articleEnEdition.observations || ''}
                                  onChange={e => setArticleEnEdition({ ...articleEnEdition, observations: e.target.value })}
                                  placeholder="Observations (optionnel)"
                                  style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }} />
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button onClick={handleModifierArticle} disabled={savingArticle}
                                    style={{ background: '#388e3c', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                    💾 Sauvegarder
                                  </button>
                                  <button onClick={() => setArticleEnEdition(null)}
                                    style={{ background: '#757575', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12 }}>
                                    Annuler
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                    {voitMontants && totalEstime > 0 && (
                      <tfoot>
                        <tr style={{ background: '#e8eaf6', fontWeight: 700 }}>
                          <td colSpan={4} style={{ padding: '10px 12px', textAlign: 'right', color: '#333' }}>TOTAL ESTIMÉ :</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#1a237e', fontSize: 15 }}>
                            {totalEstime.toLocaleString('fr-FR')} FCFA
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                )}
              </>
            ) : (
              /* ── Version lecture seule (autres statuts) ── */
              <>
                <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.primary, fontWeight: 700 }}>📦 Articles demandés</h3>
                <SectionArticles dossier={dossier} lignes={lignes} voitMontants={voitMontants} />
              </>
            )}
          </div>


        {/* Bloc suivi exécution - visible si EN_EXECUTION */}
        {dossier.statut === 'EN_EXECUTION' && (
          <div style={{
            background: '#e3f2fd',
            border: '2px solid #1976d2',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div>
              <div style={{ fontWeight: '700', color: '#1565c0', fontSize: '15px' }}>
                📊 Travaux en cours d'exécution
              </div>
              <div style={{ color: '#555', fontSize: '13px', marginTop: '4px' }}>
                Avancement : {dossier.pourcentage_avancement || 0}%
              </div>
            </div>
            <button
              onClick={() => navigate(`/achats/dossiers/${dossier.id}/suivi`)}
              style={{
                background: '#1976d2', color: 'white',
                border: 'none', borderRadius: '8px',
                padding: '10px 20px', cursor: 'pointer',
                fontSize: '14px', fontWeight: '600'
              }}
            >
              📋 Voir le suivi complet →
            </button>
          </div>
        )}

          {/* ===== BLOC EDITION BROUILLON ===== */}
          {peutEditerBrouillon && (
            <div style={{
              background: modeEdition ? '#fffde7' : '#f5f5f5',
              border: modeEdition ? '2px solid #fbc02d' : '1px solid #e0e0e0',
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '16px'
            }}>
              {!modeEdition ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, fontSize: '13px', color: '#666' }}>
                    📝 Dossier en brouillon — modifiez-le avant de soumettre
                  </div>
                  <button
                    onClick={ouvrirEdition}
                    style={{
                      background: '#1976d2', color: 'white', border: 'none',
                      borderRadius: '8px', padding: '8px 16px',
                      cursor: 'pointer', fontSize: '13px', fontWeight: '600'
                    }}
                  >
                    ✏️ Modifier
                  </button>
                  <button
                    onClick={supprimerDossier}
                    style={{
                      background: 'white', color: '#d32f2f',
                      border: '1px solid #d32f2f', borderRadius: '8px',
                      padding: '8px 16px', cursor: 'pointer',
                      fontSize: '13px', fontWeight: '600'
                    }}
                  >
                    🗑️ Supprimer
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '16px', color: '#f57f17' }}>
                    ✏️ Modification du dossier
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>
                      Objet / Description *
                    </label>
                    <input
                      type="text"
                      value={formEdition.objet}
                      onChange={e => setFormEdition({ ...formEdition, objet: e.target.value })}
                      style={{
                        width: '100%', padding: '10px', borderRadius: '8px',
                        border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box'
                      }}
                      placeholder="Ex: Achat de matériels informatiques"
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>
                        Type de dossier
                      </label>
                      <select
                        value={formEdition.type_dossier}
                        onChange={e => setFormEdition({ ...formEdition, type_dossier: e.target.value })}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' }}
                      >
                        <option value="ACHAT">🛒 Achat</option>
                        <option value="TRAVAUX">🔧 Travaux</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input
                          type="checkbox"
                          checked={formEdition.urgent}
                          onChange={e => setFormEdition({ ...formEdition, urgent: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ color: formEdition.urgent ? '#d32f2f' : '#555' }}>
                          ⚡ Marquer comme urgent
                        </span>
                      </label>
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>
                      Justification / Motif de la demande
                    </label>
                    <textarea
                      value={formEdition.justification}
                      onChange={e => setFormEdition({ ...formEdition, justification: e.target.value })}
                      rows={3}
                      placeholder="Expliquez pourquoi cet achat ou ces travaux sont nécessaires..."
                      style={{
                        width: '100%', padding: '10px', borderRadius: '8px',
                        border: '1px solid #ddd', fontSize: '13px',
                        resize: 'vertical', boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={sauvegarderEdition}
                      disabled={savingEdition}
                      style={{
                        background: '#388e3c', color: 'white', border: 'none',
                        borderRadius: '8px', padding: '10px 20px',
                        cursor: savingEdition ? 'not-allowed' : 'pointer',
                        fontSize: '14px', fontWeight: '600', opacity: savingEdition ? 0.7 : 1
                      }}
                    >
                      {savingEdition ? '⏳ Sauvegarde...' : '💾 Sauvegarder'}
                    </button>
                    <button
                      onClick={() => setModeEdition(false)}
                      style={{
                        background: '#757575', color: 'white', border: 'none',
                        borderRadius: '8px', padding: '10px 20px',
                        cursor: 'pointer', fontSize: '14px'
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* ===== FIN BLOC EDITION BROUILLON ===== */}

          {/* SECTION 4 : Proformats */}
          {['prepose', 'responsable_preposes'].includes(monRole) ? (
            <div style={{ background: C.white, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 15, color: C.primary, fontWeight: 700 }}>📄 Proformats</h3>
              <div style={{ fontSize: 13, color: C.gray, fontStyle: 'italic' }}>
                📋 {(dossier.proformats || []).length} offre(s) reçue(s) — en cours de traitement par le service achats
              </div>
            </div>
          ) : (
            <div style={{ background: C.white, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.primary, fontWeight: 700 }}>
                📄 Proformats{' '}
                <span style={{ color: (dossier.proformats || []).length >= seuilProformats ? C.success : C.danger, fontWeight: 700 }}>
                  {(dossier.proformats || []).length}/{seuilProformats} requis
                </span>
              </h3>
              <SectionProformats
                dossier={dossier}
                seuilProformats={seuilProformats}
                monRole={monRole}
                dossierId={dossier?.id}
                onRefresh={charger}
                showFormProformat={showFormProformat}
                setShowFormProformat={setShowFormProformat}
                formProformat={formProformat}
                setFormProformat={setFormProformat}
                fournisseurs={fournisseurs}
                navigate={navigate}
                avisControle={avisControle}
                voitMontants={voitMontants}
                peutVoirPDF={peutVoirProformatPDF}
                peutEcrire={peut('achats', 'ecrire')}
                proformatEnEdition={proformatEnEdition}
                setProformatEnEdition={setProformatEnEdition}
                onModifier={handleModifierProformat}
                onSupprimer={handleSupprimerProformat}
                onSauvegarder={handleSauvegarderModification}
              />
            </div>
          )}

          {/* SECTION 4b : Contrôle Proformats (EN_CONTROLE uniquement) */}
          {dossier.statut === 'EN_CONTROLE' && ['verificateur', 'controle_gestion', 'admin', 'super_admin'].includes(monRole) && (
            <div style={{ background: C.white, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#283593', fontWeight: 700 }}>🔍 Analyse des proformats</h3>
              <SectionControleProformats
                dossier={dossier}
                dossierId={dossier?.id}
                monRole={monRole}
                avisParProformat={avisParProformat}
                setAvisParProformat={setAvisParProformat}
                commentaireControle={commentaireControle}
                setCommentaireControle={setCommentaireControle}
                onRefresh={charger}
              />
            </div>
          )}

          {/* SECTION 4c : Vérification comptable (EN_COMPTABILITE uniquement) */}
          {monRole === 'comptable' && dossier.statut === 'EN_COMPTABILITE' && (
            <div style={{ background: '#e8eaf6', border: '1px solid #5c6bc0', borderRadius: 10,
              padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#283593', fontWeight: 700 }}>
                📋 Documents à vérifier avant approbation
              </h3>
              {(dossier.proformats || []).length === 0 ? (
                <div style={{ color: '#999', fontStyle: 'italic', fontSize: 13 }}>Aucun proformat joint à ce dossier</div>
              ) : (
                (dossier.proformats || []).map(p => (
                  <div key={p.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', background: 'white', borderRadius: 6, marginBottom: 8,
                    border: p.selectionne ? '2px solid #4caf50' : '1px solid #e0e0e0', flexWrap: 'wrap', gap: 8,
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {p.fournisseur_nom_libre || p.fournisseur_nom || `Proformat #${p.id}`}
                        {p.selectionne && (
                          <span style={{ marginLeft: 8, background: '#4caf50', color: 'white',
                            fontSize: 10, padding: '2px 6px', borderRadius: 10 }}>✅ RETENU</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {p.montant_ttc ? Number(p.montant_ttc).toLocaleString('fr-FR') + ' FCFA' : '—'}
                        {p.numero_proformat && ` · N° ${p.numero_proformat}`}
                      </div>
                    </div>
                    {p.fichier_url ? (
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <a href={p.fichier_url} target="_blank" rel="noreferrer"
                          style={{ background: '#1565c0', color: 'white', padding: '6px 12px',
                            borderRadius: 6, textDecoration: 'none', fontSize: 12, fontWeight: 600 }}>
                          📄 Voir
                        </a>
                        <a href={p.fichier_url} download
                          style={{ background: '#2e7d32', color: 'white', padding: '6px 12px',
                            borderRadius: 6, textDecoration: 'none', fontSize: 12, fontWeight: 600 }}>
                          ⬇️ Télécharger
                        </a>
                      </div>
                    ) : (
                      <span style={{ color: '#f44336', fontSize: 12, fontStyle: 'italic' }}>⚠️ Pas de PDF</span>
                    )}
                  </div>
                ))
              )}
              <div style={{ marginTop: 12, padding: 10, background: 'white', borderRadius: 6, fontSize: 12, color: '#555' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>✅ Checklist comptable :</div>
                <div>☐ Proformat retenu consulté et vérifié</div>
                <div>☐ Montant cohérent avec le budget</div>
                <div>☐ Fournisseur référencé ou justifié</div>
                <div>☐ Documents complets</div>
              </div>
            </div>
          )}

          {/* SECTION 5 : Documents (masqué pour préposé) */}
          {!['prepose', 'responsable_preposes'].includes(monRole) && (
            <div style={{ background: C.white, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.primary, fontWeight: 700 }}>📎 Documents</h3>
              <SectionDocuments
                dossier={dossier}
                proformats={dossier.proformats || []}
                peutEcrire={peut('achats', 'ecrire')}
                onRefresh={charger}
              />
            </div>
          )}

        </div>

        {/* ── Colonne droite — réservée (supprimée) ── */}
      </div>

      {/* ── SECTION 5b : Reçu de paiement ─────────────────────────────── */}
      {['PAYE', 'CLOTURE'].includes(dossier.statut) &&
       ['comptable','controle_gestion','direction','admin','super_admin'].includes(monRole) && (() => {
        const lignePaye = (dossier.historique || []).find(h => h.statut_apres === 'PAYE');
        const encaissePar = lignePaye?.utilisateur_nom || '—';
        const beneficiaire = dossier.fournisseur_retenu_nom
          || (dossier.proformats || []).find(p => p.selectionne)?.fournisseur_nom
          || '—';
        const montant = (dossier.montant_total || dossier.montant_estime || 0).toLocaleString('fr-FR');
        const modeLabel = dossier.mode_paiement === 'especes' ? '💵 Espèces' : dossier.mode_paiement === 'cheque' ? '🏦 Chèque' : '—';
        const couleurBg = dossier.mode_paiement === 'especes' ? '#fffde7' : dossier.mode_paiement === 'cheque' ? '#e8eaf6' : '#f5f5f5';
        const couleurBorder = dossier.mode_paiement === 'especes' ? '#ffc107' : dossier.mode_paiement === 'cheque' ? '#9fa8da' : '#e0e0e0';
        return (
          <div style={{ margin: '0 24px 20px', background: couleurBg, border: `1px solid ${couleurBorder}`, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#333', fontWeight: 700 }}>🧾 Reçu de paiement</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 24px' }}>
              <div>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>Mode</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#333' }}>{modeLabel}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>Montant</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1b5e20' }}>{montant} FCFA</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>Bénéficiaire</div>
                <div style={{ fontSize: 14, color: '#333' }}>{beneficiaire}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>Date de paiement</div>
                <div style={{ fontSize: 14, color: '#333' }}>{dossier.date_paiement ? new Date(dossier.date_paiement).toLocaleDateString('fr-FR') : '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>Encaissé par</div>
                <div style={{ fontSize: 14, color: '#333' }}>{encaissePar}</div>
              </div>
              {dossier.numero_recu && (
                <div>
                  <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>N° Reçu</div>
                  <div style={{ fontSize: 14, color: '#333' }}>{dossier.numero_recu}</div>
                </div>
              )}
              {dossier.mode_paiement === 'cheque' && dossier.numero_cheque && (
                <div>
                  <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>N° Chèque</div>
                  <div style={{ fontSize: 14, color: '#333' }}>{dossier.numero_cheque}</div>
                </div>
              )}
              {dossier.mode_paiement === 'cheque' && dossier.banque_cheque && (
                <div>
                  <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>Banque</div>
                  <div style={{ fontSize: 14, color: '#333' }}>{dossier.banque_cheque}</div>
                </div>
              )}
              {dossier.mode_paiement === 'cheque' && dossier.date_cheque && (
                <div>
                  <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>Date chèque</div>
                  <div style={{ fontSize: 14, color: '#333' }}>{new Date(dossier.date_cheque).toLocaleDateString('fr-FR')}</div>
                </div>
              )}
            </div>
            {dossier.recu_paiement_url && (
              <div style={{ marginTop: 16 }}>
                <a
                  href={dossier.recu_paiement_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: `1px solid ${couleurBorder}`, borderRadius: 6, padding: '7px 14px', fontSize: 13, color: '#1565c0', textDecoration: 'none', fontWeight: 600 }}>
                  📄 Voir reçu signé
                </a>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── SECTION 6 : Suivi & Commentaires ──────────────────────────── */}
      {commentairesAffiches.length > 0 && (
        <div style={{ margin: '0 24px 20px', background: C.white, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, color: C.primary, fontWeight: 700 }}>
            💬 Suivi & Commentaires ({commentairesAffiches.length})
          </h3>
          {commentairesAffiches.map((h, i) => {
            const styleC = getStyleCommentaire(h);
            return (
              <div key={i} style={{ padding: '12px 14px', borderRadius: 8, marginBottom: 8, ...styleC }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, color: '#333' }}>{h.utilisateur_nom || 'Système'}</span>
                  <span style={{ color: '#888' }}>
                    {h.statut_avant} → {h.statut_apres}
                    {' · '}
                    {new Date(h.created_at).toLocaleDateString('fr-FR')}
                    {' '}
                    {new Date(h.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div style={{ fontStyle: 'italic', color: '#444', fontSize: 13 }}>
                  "{h.commentaire}"
                </div>
              </div>
            );
          })}

          {/* Zone commentaire pour l'étape EN_COMPTABILITE */}
          {(peut('achats', 'approuver') || monRole === 'comptable') && dossier.statut === 'EN_COMPTABILITE' && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase' }}>
                Votre commentaire :
              </div>
              <textarea
                value={commentaireControle}
                onChange={e => setCommentaireControle(e.target.value)}
                placeholder="Observations comptables, réserves, notes pour la direction..."
                style={{ width: '100%', minHeight: 80, padding: 10, borderRadius: 8, border: '1px solid #ddd',
                  fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
          )}
        </div>
      )}

      {/* ── SECTION 7 : Historique ──────────────────────────────────────── */}
      <div style={{ margin: '0 24px 20px', background: C.white, borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.primary, fontWeight: 700 }}>
          📋 Historique ({historique.length})
        </h3>
        <SectionHistorique historique={historique} />
      </div>

      {/* ── SECTION 7 : Barre d'action fixe ────────────────────────────── */}
      <div style={{ position: 'fixed', bottom: 0, left: 220, right: 0, background: C.white,
        borderTop: `1px solid ${C.border}`, padding: '12px 24px', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.06)', zIndex: 100 }}>
        <div style={{ fontSize: 13, color: C.gray }}>
          Dossier <strong>{dossier.numero}</strong> — <StatutBadge statut={dossier.statut} />
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={() => navigate('/achats')}
            style={{ background: C.white, border: `1px solid ${C.border}`, color: C.gray,
              padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
            ← Retour liste
          </button>
          {['PAYE','CLOTURE'].includes(dossier.statut) && ['direction','admin','super_admin','comptable','controle_gestion'].includes(monRole) && (
            <button onClick={handleVoirRapport}
              style={{ background: '#e8eaf6', border: '1px solid #9fa8da', color: '#3949ab',
                padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              📄 Rapport complet
            </button>
          )}
          {getBoutonAction()}
        </div>
      </div>

      {/* ── MODAL RÉCEPTION FACTURE ─────────────────────────────────────── */}
      {showModalFacture && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 500, width: '100%', padding: 24, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>

            {/* Titre */}
            <div style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.textDark }}>📄 Réception de la facture fournisseur</div>
            </div>
            <div style={{ fontSize: 12, color: C.textMid, marginBottom: 20 }}>
              Dossier {dossier.numero}
              {dossier.fournisseur_retenu_nom && ` — Fournisseur retenu : ${dossier.fournisseur_retenu_nom}`}
              {!dossier.fournisseur_retenu_nom && (() => {
                const pSel = (dossier.proformats || []).find(p => p.selectionne);
                return pSel ? ` — Fournisseur retenu : ${pSel.fournisseur_nom || pSel.fournisseur_nom_libre || '—'}` : '';
              })()}
            </div>

            {/* Champ 1 : Fichier (obligatoire) */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>
                Facture fournisseur <span style={{ color: C.danger }}>*</span>
              </label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => { setFormFacture(f => ({ ...f, fichier: e.target.files[0] || null })); setErreurFacture(''); }}
                style={{ width: '100%', fontSize: 13, padding: '6px 0', cursor: 'pointer' }}
              />
              {erreurFacture && (
                <div style={{ color: '#C62828', fontSize: 12, marginTop: 5 }}>
                  ⚠ {erreurFacture}
                </div>
              )}
            </div>

            {/* Champ 2 : Numéro de facture */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>Numéro de facture</label>
              <input
                type="text"
                value={formFacture.numero_facture}
                onChange={e => setFormFacture(f => ({ ...f, numero_facture: e.target.value }))}
                placeholder="Ex: FACT-2026-0089"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>

            {/* Champ 3 : Montant facturé */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>Montant facturé (FCFA)</label>
              <input
                type="number"
                min="0"
                value={formFacture.montant_ttc}
                onChange={e => setFormFacture(f => ({ ...f, montant_ttc: e.target.value }))}
                placeholder="0"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>

            {/* Champ 4 : Commentaire */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>Commentaire</label>
              <textarea
                rows={3}
                value={formFacture.commentaire}
                onChange={e => setFormFacture(f => ({ ...f, commentaire: e.target.value }))}
                placeholder="Observations sur la facture reçue..."
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModalFacture(false)}
                disabled={savingFacture}
                style={{ padding: '9px 20px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: 13, color: C.textMid }}
              >
                Annuler
              </button>
              <button
                disabled={savingFacture}
                onClick={async () => {
                  if (!formFacture.fichier) { setErreurFacture('Veuillez sélectionner la facture fournisseur avant de confirmer'); return; }
                  setSavingFacture(true);
                  try {
                    const fd = new FormData();
                    fd.append('facture', formFacture.fichier);
                    if (formFacture.numero_facture) fd.append('numero_facture', formFacture.numero_facture);
                    if (formFacture.montant_ttc)    fd.append('montant_ttc',    formFacture.montant_ttc);
                    await api.post(`/achats/dossiers/${dossier.id}/facture`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    const commentaire = formFacture.commentaire || 'Facture fournisseur reçue';
                    await api.post(`/achats/dossiers/${dossier.id}/transition`, { nouveau_statut: 'FACTURE_RECUE', commentaire });
                    setShowModalFacture(false);
                    charger();
                  } catch (e) {
                    setErreurFacture(e.response?.data?.message || 'Erreur lors de l\'enregistrement');
                  } finally {
                    setSavingFacture(false);
                  }
                }}
                style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: savingFacture ? '#aaa' : C.primary, color: '#fff', cursor: savingFacture ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                {savingFacture ? '⏳ Envoi…' : 'Confirmer la réception →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL CLÔTURE ───────────────────────────────────────────────── */}
      {showModalCloture && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '28px 32px', width: 480, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 18, color: '#333' }}>🔒 Clôture du dossier {dossier.numero}</h2>
            <p style={{ margin: '0 0 18px', fontSize: 13, color: '#e53935', fontWeight: 600 }}>⚠️ Cette action est irréversible.</p>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>
              Observations finales / Rapport de clôture *
            </label>
            <textarea
              value={commentaireCloture}
              onChange={e => setCommentaireCloture(e.target.value)}
              placeholder="Résumé de l'opération, conformité, observations..."
              rows={5}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ccc', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 18 }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowModalCloture(false); setCommentaireCloture(''); }}
                style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
                Annuler
              </button>
              <button onClick={handleCloturer} disabled={savingCloture || !commentaireCloture.trim()}
                style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: savingCloture || !commentaireCloture.trim() ? '#aaa' : '#455a64', color: '#fff', cursor: savingCloture || !commentaireCloture.trim() ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                {savingCloture ? '⏳ En cours…' : '🔒 Confirmer la clôture'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL RAPPORT COMPLET ────────────────────────────────────────── */}
      {showModalRapport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, overflowY: 'auto', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 900, margin: '0 auto', padding: '32px', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} id="rapport-print-zone">
            {/* En-tête */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <h2 style={{ margin: '0 0 4px', fontSize: 20, color: '#1a237e' }}>📋 Rapport de dossier d'achat</h2>
                {rapportData && <div style={{ fontSize: 13, color: '#666' }}>{rapportData.dossier?.numero} — {rapportData.dossier?.objet}</div>}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                {['direction','admin','super_admin'].includes(monRole) && (
                  <button onClick={handleTelechargerZip} disabled={telechargementZip}
                    style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #9fa8da', background: '#e8eaf6', color: '#3949ab', cursor: telechargementZip ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600 }}>
                    {telechargementZip ? '⏳ ZIP…' : '📦 Télécharger ZIP'}
                  </button>
                )}
                <button onClick={() => window.print()}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#f5f5f5', cursor: 'pointer', fontSize: 12 }}>
                  🖨️ Imprimer
                </button>
                <button onClick={() => setShowModalRapport(false)}
                  style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#455a64', color: '#fff', cursor: 'pointer', fontSize: 12 }}>
                  ✕ Fermer
                </button>
              </div>
            </div>

            {loadingRapport && <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>⏳ Chargement du rapport…</div>}

            {rapportData && (() => {
              const R = rapportData;
              const thStyle = { background: '#e8eaf6', padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#3949ab', textAlign: 'left', borderBottom: '2px solid #9fa8da' };
              const tdStyle = { padding: '8px 12px', fontSize: 13, borderBottom: '1px solid #eee', verticalAlign: 'top' };
              const sectionTitle = (icon, title) => (
                <h3 style={{ margin: '24px 0 10px', fontSize: 14, color: '#1a237e', borderLeft: '4px solid #3949ab', paddingLeft: 10 }}>{icon} {title}</h3>
              );
              return (
                <div>
                  {/* Section 1 : Identification */}
                  {sectionTitle('🗂️', 'Identification du dossier')}
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                    <tbody>
                      {[
                        ['Numéro', R.dossier.numero], ['Type', R.dossier.type],
                        ['Objet', R.dossier.objet], ['Abattoir', R.dossier.abattoir],
                        ['Statut', R.dossier.statut], ['Urgence', R.dossier.urgence ? '🔴 Oui' : 'Non'],
                        ['Demandeur', R.demandeur.nom], ['Rôle demandeur', R.demandeur.role],
                        ['Date création', R.dossier.date_creation ? new Date(R.dossier.date_creation).toLocaleDateString('fr-FR') : '—'],
                        ['Montant estimé', R.montant_estime ? Number(R.montant_estime).toLocaleString('fr-FR') + ' FCFA' : '—'],
                        ['Montant total', R.montant_total ? Number(R.montant_total).toLocaleString('fr-FR') + ' FCFA' : '—'],
                      ].map(([k, v], i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                          <td style={{ ...tdStyle, fontWeight: 600, color: '#555', width: '35%' }}>{k}</td>
                          <td style={tdStyle}>{v || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Section 2 : Articles */}
                  {R.articles?.length > 0 && <>
                    {sectionTitle('📦', 'Articles demandés')}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                      <thead><tr>
                        {['Désignation','Qté','Unité','P.U. estimé','Total estimé','Obs.'].map(h => <th key={h} style={thStyle}>{h}</th>)}
                      </tr></thead>
                      <tbody>{R.articles.map((a, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                          <td style={tdStyle}>{a.designation}</td>
                          <td style={tdStyle}>{a.qte}</td>
                          <td style={tdStyle}>{a.unite}</td>
                          <td style={tdStyle}>{a.prix_unitaire ? Number(a.prix_unitaire).toLocaleString('fr-FR') : '—'}</td>
                          <td style={tdStyle}>{a.total ? Number(a.total).toLocaleString('fr-FR') : '—'}</td>
                          <td style={tdStyle}>{a.observations || '—'}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </>}

                  {/* Section 3 : Circuit de validation */}
                  {sectionTitle('🔄', 'Circuit de validation')}
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                    <thead><tr>
                      {['N°','Étape','Validé par','Rôle','Date','Commentaire'].map(h => <th key={h} style={thStyle}>{h}</th>)}
                    </tr></thead>
                    <tbody>{R.workflow.map((w, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700 }}>{w.etape}</td>
                        <td style={tdStyle}><span style={{ fontSize: 11 }}>{w.statut_avant}</span> → <strong style={{ fontSize: 11 }}>{w.statut_apres}</strong></td>
                        <td style={tdStyle}>{w.acteur}</td>
                        <td style={tdStyle}>{w.role_acteur}</td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{w.date ? new Date(w.date).toLocaleDateString('fr-FR') : '—'}</td>
                        <td style={{ ...tdStyle, fontStyle: w.commentaire ? 'normal' : 'italic', color: w.commentaire ? '#333' : '#aaa' }}>{w.commentaire || 'Aucun'}</td>
                      </tr>
                    ))}</tbody>
                  </table>

                  {/* Section 4 : Proformats */}
                  {R.proformats?.length > 0 && <>
                    {sectionTitle('📋', 'Proformats reçus')}
                    {R.proformats.map((p, i) => (
                      <div key={i} style={{ border: p.retenu ? '2px solid #43a047' : '1px solid #e0e0e0', borderRadius: 8, padding: '10px 14px', marginBottom: 8, background: p.retenu ? '#f1f8e9' : '#fafafa' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{p.numero} — {p.fournisseur}</span>
                          <span style={{ fontSize: 13 }}>
                            {p.montant ? Number(p.montant).toLocaleString('fr-FR') + ' FCFA' : '—'}
                            {p.retenu && <span style={{ marginLeft: 8, background: '#43a047', color: '#fff', borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>✓ Retenu</span>}
                          </span>
                        </div>
                        {p.avis_controleur?.length > 0 && p.avis_controleur.map((av, j) => (
                          <div key={j} style={{ fontSize: 12, color: '#555', background: '#fff', borderRadius: 4, padding: '4px 8px', marginTop: 4 }}>
                            <strong>{av.auteur}</strong> : {av.verdict} {av.commentaire ? `— ${av.commentaire}` : ''}
                          </div>
                        ))}
                      </div>
                    ))}
                  </>}

                  {/* Section 5 : Documents joints */}
                  {sectionTitle('📎', 'Documents joints')}
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                    <tbody>
                      {[
                        ['Bordereau de livraison', R.documents.bordereau_livraison],
                        ['Facture fournisseur', R.documents.facture_fournisseur],
                        ['Reçu de paiement', R.documents.recu_paiement],
                      ].map(([label, doc], i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                          <td style={{ ...tdStyle, fontWeight: 600, width: '40%' }}>{label}</td>
                          <td style={tdStyle}>
                            {doc.joint ? (
                              <span style={{ color: '#2e7d32', fontWeight: 600 }}>✅ Joint{doc.url && <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8, fontSize: 12, color: '#1565c0' }}>Voir</a>}</span>
                            ) : (
                              <span style={{ color: '#c62828' }}>❌ Non joint</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Section 6 : Paiement */}
                  {sectionTitle('💰', 'Paiement')}
                  <div style={{ background: R.paiement.mode === 'cheque' ? '#e8eaf6' : '#fffde7', border: '1px solid #e0e0e0', borderRadius: 8, padding: '12px 16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px 20px' }}>
                      {[
                        ['Mode', R.paiement.mode === 'especes' ? '💵 Espèces' : R.paiement.mode === 'cheque' ? '🏦 Chèque' : '—'],
                        ['Montant', R.paiement.montant ? Number(R.paiement.montant).toLocaleString('fr-FR') + ' FCFA' : '—'],
                        ['Bénéficiaire', R.paiement.beneficiaire],
                        ['Encaissé par', R.paiement.encaisse_par],
                        ['Date paiement', R.paiement.date_paiement ? new Date(R.paiement.date_paiement).toLocaleDateString('fr-FR') : '—'],
                        ['N° Reçu', R.paiement.numero_recu || '—'],
                        ...(R.paiement.mode === 'cheque' ? [
                          ['N° Chèque', R.paiement.numero_cheque || '—'],
                          ['Banque', R.paiement.banque_cheque || '—'],
                          ['Date chèque', R.paiement.date_cheque ? new Date(R.paiement.date_cheque).toLocaleDateString('fr-FR') : '—'],
                        ] : []),
                      ].map(([k, v], i) => (
                        <div key={i}>
                          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{k}</div>
                          <div style={{ fontSize: 13, color: '#333', fontWeight: 600 }}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Section 7 : Clôture */}
                  {R.cloture && <>
                    {sectionTitle('🔒', 'Clôture')}
                    <div style={{ background: '#efebe9', border: '1px solid #bcaaa4', borderRadius: 8, padding: '12px 16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 20px', marginBottom: 10 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Date de clôture</div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{R.cloture.date_cloture ? new Date(R.cloture.date_cloture).toLocaleDateString('fr-FR') : '—'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Clôturé par</div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{R.cloture.cloture_par}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Observations</div>
                      <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap' }}>{R.cloture.commentaire_cloture}</div>
                    </div>
                  </>}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
